import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, Tooltip, Legend, Filler,
} from 'chart.js';
import { Bar, Chart, Line } from 'react-chartjs-2';
import {
  DEFAULT_METRIC_SETTINGS,
  calculatePropertyMetrics,
  normalizeMetricSettings,
} from '../utils/propertyMetrics';
import { apiJson, apiUrl } from '../services/api';
import PageLoader from '../components/PageLoader';
import { StatementReviewPanel } from './StatementReview';
import './PropertyDetail.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend, Filler);
const fmt = (n) => n == null ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtNum = (n) => n == null ? '—' : Number(n).toLocaleString();
const INCOME_EXPENSE_RANGE_OPTIONS = [
  { value: '18', label: '18 months' },
  { value: '12', label: '12 months' },
  { value: '6', label: '6 months' },
];
const PM_FEE_AUDIT_RANGE_OPTIONS = [
  { value: '12', label: '12 months' },
  { value: '18', label: '18 months' },
  { value: '24', label: '24 months' },
  { value: '36', label: '36 months' },
];
const MANAGEMENT_FEE_RATE = 0.07;
const MANAGEMENT_FEE_TOLERANCE = 1.5;
const RENTCAST_USAGE_WINDOW_DAYS = 30;
const RENTCAST_CALLS_PER_FULL_REFRESH = 2;
const PROPERTY_TYPE_OPTIONS = [
  { value: 'single_family', label: 'Single Family' },
  { value: 'multi_family', label: 'Multi Family' },
  { value: 'condo', label: 'Condo' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'commercial', label: 'Commercial' },
];
const EXPENSE_TREND_COLORS = ['#1D4ED8', '#16A34A', '#F59E0B', '#EF4444', '#8B5CF6', '#64748B'];
const RENT_BENCHMARK_COLORS = {
  collected: '#16A34A',
  contract: '#1D4ED8',
  market: '#7C3AED',
};

function buildRentCastUrl(property) {
  const address = [
    property?.address_line1,
    property?.address_line2,
    property?.city,
    property?.state,
    property?.zip,
  ].filter(Boolean).join(', ');

  const params = [];
  const addParam = (key, value) => {
    if (value === null || value === undefined || value === '') return;
    params.push(`${key}=${encodeURIComponent(String(value)).replace(/%2C/g, ',')}`);
  };

  addParam('address', address);
  addParam('type', property?.property_type?.replaceAll('_', '-'));
  addParam('bedrooms', property?.bedrooms);
  addParam('bathrooms', property?.bathrooms);
  addParam('area', property?.square_feet);
  addParam('year', property?.year_built);

  return `https://app.rentcast.io/app${params.length ? `?${params.join('&')}` : ''}`;
}

function buildLoanForm(property) {
  const loanTermMonths = property?.loan_term_months != null ? Number(property.loan_term_months) : null;
  const loanTermYears = Number.isFinite(loanTermMonths) && loanTermMonths > 0
    ? loanTermMonths / 12
    : null;

  return {
    lender_name: property?.lender_name || '',
    original_principal: property?.original_principal != null ? String(property.original_principal) : '',
    interest_rate: property?.interest_rate != null ? String(property.interest_rate) : '',
    loan_term_years: loanTermYears != null
      ? String(Number.isInteger(loanTermYears) ? loanTermYears : Number(loanTermYears.toFixed(2)))
      : '',
    start_date: (() => {
      if (!property?.loan_start_date) return '';
      const d = new Date(property.loan_start_date);
      if (isNaN(d.getTime())) return '';
      const y = d.getUTCFullYear();
      const m = String(d.getUTCMonth() + 1).padStart(2, '0');
      const day = String(d.getUTCDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    })(),
    monthly_payment: property?.monthly_payment != null ? String(property.monthly_payment) : '',
    notes: property?.loan_notes || '',
  };
}

function formatDateInputValue(value) {
  if (!value) return '';
  const dateOnly = String(value).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return dateOnly;
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return '';
  const year = parsed.getUTCFullYear();
  const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const day = String(parsed.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function buildPropertyForm(property) {
  return {
    nickname: property?.nickname || '',
    address_line1: property?.address_line1 || '',
    address_line2: property?.address_line2 || '',
    city: property?.city || '',
    state: property?.state || '',
    zip: property?.zip || '',
    property_type: property?.property_type || 'single_family',
    bedrooms: property?.bedrooms != null ? String(property.bedrooms) : '',
    bathrooms: property?.bathrooms != null ? String(property.bathrooms) : '',
    square_feet: property?.square_feet != null ? String(property.square_feet) : '',
    year_built: property?.year_built != null ? String(property.year_built) : '',
    purchase_price: property?.purchase_price != null ? String(property.purchase_price) : '',
    asking_price: property?.asking_price != null ? String(property.asking_price) : '',
    purchase_date: formatDateInputValue(property?.purchase_date),
    current_value: property?.current_value != null ? String(property.current_value) : '',
    notes: property?.notes || '',
  };
}

function formatMonthLabel(month) {
  if (!month) return '—';
  return new Date(`${month}-02T00:00:00`).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function monthKeyFromDate(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return null;
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

function monthKeyFromValue(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}$/.test(raw)) return raw;
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 7);
  return monthKeyFromDate(value instanceof Date ? value : new Date(value));
}

function shiftMonthKey(month, delta) {
  if (!month) return null;
  const match = String(month).match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;
  const shifted = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1 + delta, 1));
  return monthKeyFromDate(shifted);
}

function buildMonthWindow(endMonth, count) {
  if (!endMonth || !Number.isFinite(count) || count <= 0) return [];
  const months = [];
  for (let offset = count - 1; offset >= 0; offset -= 1) {
    const month = shiftMonthKey(endMonth, -offset);
    if (month) months.push(month);
  }
  return months;
}

function computeRentcastEstimatedCalls(activePropertyCount, refreshFrequencyDays, windowDays = RENTCAST_USAGE_WINDOW_DAYS) {
  const propertyCount = Number.isFinite(activePropertyCount) ? Math.max(0, activePropertyCount) : 0;
  const cadenceDays = Number.isFinite(refreshFrequencyDays) ? Math.max(1, refreshFrequencyDays) : 30;
  if (propertyCount <= 0) return 0;
  const refreshCycles = Math.max(1, Math.ceil(windowDays / cadenceDays));
  return propertyCount * RENTCAST_CALLS_PER_FULL_REFRESH * refreshCycles;
}

function formatCategoryLabel(category) {
  return String(category || 'other')
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatPct(value) {
  return value == null ? '—' : `${Number(value).toFixed(1)}%`;
}

function roundMoney(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

function normalizeFeeAuditText(...values) {
  return values
    .map((value) => String(value || '').toLowerCase())
    .join(' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function classifyPmFeeExpense(row) {
  const category = String(row?.category || '').toLowerCase();
  const text = normalizeFeeAuditText(row?.category, row?.vendor, row?.description);

  if (!text && category !== 'management') return null;

  if (text.includes('eviction')) return 'eviction';
  if (text.includes('renewal') || text.includes('lease renewal')) return 'renewal';
  if (text.includes('advertis') || text.includes('marketing')) return 'advertising';

  if (
    category === 'management'
    || text.includes('management fee')
    || text.includes('mgmt fee')
    || text.includes('monthly management')
    || text.includes('property management fee')
    || text.includes('pm fee')
  ) {
    return 'management';
  }

  if (
    text.includes('property manager')
    || text.includes('property management')
    || text.includes('management company')
    || text.includes('leasing fee')
    || text.includes('leasing')
    || text.includes('lease up')
    || text.includes('lease-up')
    || text.includes('tenant placement')
    || text.includes('admin fee')
    || text.includes('administrative')
    || text.includes('inspection fee')
    || text.includes('coordination fee')
    || text.includes('notice fee')
    || text.includes('posting fee')
    || text.includes('setup fee')
    || text.includes('lease signing')
  ) {
    return 'other';
  }

  return null;
}

function buildPmFeeAuditStatus(expectedFee, actualFee, managementEntries) {
  const roundedExpected = roundMoney(expectedFee);
  const roundedActual = roundMoney(actualFee);
  const roundingTolerance = Math.max(MANAGEMENT_FEE_TOLERANCE, Math.abs(roundedExpected) * 0.02);
  const duplicateThreshold = Math.max(2, Math.abs(roundedExpected) * 0.08);
  const amountCounts = managementEntries.reduce((counts, entry) => {
    const amount = roundMoney(entry.amount);
    if (amount <= 0) return counts;
    counts[amount] = (counts[amount] || 0) + 1;
    return counts;
  }, {});
  const hasDuplicateManagementFee = Object.values(amountCounts).some((count) => count > 1);
  const looksApproximatelyDouble = roundedExpected > 0
    && Math.abs(roundedActual - (roundedExpected * 2)) <= duplicateThreshold;
  const matchesExpected = Math.abs(roundedActual - roundedExpected) <= roundingTolerance;

  if (hasDuplicateManagementFee || looksApproximatelyDouble) {
    return { label: 'Possible Duplicate', tone: 'duplicate' };
  }
  if (matchesExpected) {
    return { label: 'Normal', tone: 'normal' };
  }
  return { label: 'Review', tone: 'review' };
}

// ── Depreciation helpers ──────────────────────────────────────────────────────
function calcDepreciation(property) {
  const usefulLife = property.property_type === 'commercial' ? 39 : 27.5;
  const depreciableBase = property.purchase_price * 0.8;
  const annualDeduction = depreciableBase / usefulLife;
  const yearsHeld = (new Date() - new Date(property.purchase_date)) / (1000 * 60 * 60 * 24 * 365.25);
  const yearsRemaining = Math.max(0, usefulLife - yearsHeld);
  const accumulatedDepreciation = Math.min(depreciableBase, annualDeduction * yearsHeld);
  return { usefulLife, depreciableBase, annualDeduction, yearsHeld, yearsRemaining, accumulatedDepreciation };
}

function depreciationChartData(dep, purchaseDate) {
  const startYear = new Date(purchaseDate).getFullYear();
  const currentYear = new Date().getFullYear();
  const labels = [], values = [], pointColors = [], pointRadii = [], pointBorderWidths = [];
  for (let i = 0; i <= Math.ceil(dep.usefulLife); i++) {
    const year = startYear + i;
    const isCurrent = year === currentYear;
    labels.push(year);
    values.push(parseFloat(Math.min(dep.depreciableBase, dep.annualDeduction * i).toFixed(2)));
    pointColors.push(isCurrent ? '#7C3AED' : 'rgba(139,92,246,0.6)');
    pointRadii.push(isCurrent ? 7 : 3);
    pointBorderWidths.push(isCurrent ? 2 : 1);
  }
  return {
    labels,
    datasets: [{
      label: 'Accumulated Depreciation',
      data: values,
      borderColor: '#8B5CF6',
      borderWidth: 2.5,
      backgroundColor: (ctx) => {
        const { ctx: canvas, chartArea } = ctx.chart;
        if (!chartArea) return 'rgba(139,92,246,0.12)';
        const g = canvas.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        g.addColorStop(0, 'rgba(139,92,246,0.25)');
        g.addColorStop(1, 'rgba(139,92,246,0.02)');
        return g;
      },
      fill: true, tension: 0.35,
      pointBackgroundColor: pointColors, pointBorderColor: pointColors,
      pointRadius: pointRadii, pointBorderWidth: pointBorderWidths, pointHoverRadius: 7,
    }],
  };
}

// ── Amortization helpers ──────────────────────────────────────────────────────
function toFiniteNumber(value) {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function roundCents(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function parseLoanDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const dateOnly = String(value).slice(0, 10);
  const match = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
}

function addMonths(date, months) {
  const next = new Date(date.getFullYear(), date.getMonth(), 1);
  next.setMonth(next.getMonth() + months);
  const lastDay = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
  next.setDate(Math.min(date.getDate(), lastDay));
  return next;
}

function buildAmortization(loan) {
  if (!loan) return null;
  const P = toFiniteNumber(loan.original_principal);
  const annualRate = toFiniteNumber(loan.interest_rate);
  const n = Number.parseInt(loan.loan_term_months, 10);
  const storedMonthlyPayment = toFiniteNumber(loan.monthly_payment);
  if (P == null || annualRate == null || !Number.isInteger(n) || P <= 0 || annualRate < 0 || n <= 0) return null;

  const r = annualRate / 100 / 12;
  const payment = roundCents(r === 0 ? P / n : (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1));
  const debtServicePayment = storedMonthlyPayment != null ? storedMonthlyPayment : payment;
  const startDate = parseLoanDate(loan.loan_start_date || loan.start_date);
  if (!startDate) return null;

  const today = new Date();
  const startYear = startDate.getFullYear();
  const payoffDate = addMonths(startDate, n);
  const payoffYear = payoffDate.getFullYear();
  const currentYear = today.getFullYear();

  const annualPayments = {};
  const yearEndBalance = {};
  let balance = P;
  let monthsElapsed = 0;
  let balanceNow = P;
  let interestPaid = 0;

  for (let m = 0; m < n; m++) {
    const paymentDate = addMonths(startDate, m + 1);
    const yr = paymentDate.getFullYear();
    const ip = balance * r;
    const pp = m === n - 1 ? balance : Math.min(payment - ip, balance);

    balance = Math.max(0, balance - pp);
    if (balance < 0.005 || m === n - 1) balance = 0;

    if (!annualPayments[yr]) annualPayments[yr] = { principal: 0, interest: 0, months: 0 };
    annualPayments[yr].principal += pp;
    annualPayments[yr].interest += ip;
    annualPayments[yr].months += 1;
    yearEndBalance[yr] = roundCents(balance);

    if (paymentDate <= today) {
      monthsElapsed = m + 1;
      balanceNow = roundCents(balance);
      interestPaid += ip;
    }
  }

  const labels = ['Start'];
  const balanceValues = [P];
  const principalValues = [0];
  const interestValues = [0];
  const pointColors = ['rgba(37,99,235,0.65)'];
  const pointRadii = [3];
  const pointBorderWidths = [1];

  for (let yr = startYear; yr <= payoffYear; yr++) {
    const isToday = yr === currentYear && today >= startDate && today <= payoffDate;
    const isPayoff = yr === payoffYear;
    const scheduled = annualPayments[yr] || { principal: 0, interest: 0, months: 0 };
    const showPaymentBar = !(isPayoff && scheduled.months > 0 && scheduled.months < 12);
    const bal = isToday
      ? balanceNow
      : yearEndBalance[yr] != null
        ? yearEndBalance[yr]
        : isPayoff
          ? 0
          : P;

    labels.push(String(yr));
    balanceValues.push(bal);
    principalValues.push(showPaymentBar ? roundCents(scheduled.principal) : null);
    interestValues.push(showPaymentBar ? roundCents(scheduled.interest) : null);
    pointColors.push(isToday ? '#7C3AED' : isPayoff ? '#16A34A' : 'rgba(37,99,235,0.55)');
    pointRadii.push(isToday || isPayoff ? 7 : 3);
    pointBorderWidths.push(isToday || isPayoff ? 2 : 1);
  }

  const principalPaid = roundCents(P - balanceNow);

  const chartData = {
    labels,
    datasets: [
      {
        type: 'bar',
        label: 'Principal Paid',
        data: principalValues,
        yAxisID: 'payments',
        stack: 'payments',
        backgroundColor: 'rgba(22,163,74,0.82)',
        borderColor: 'rgba(5,150,105,0.25)',
        borderWidth: 1,
        borderRadius: 4,
        maxBarThickness: 18,
        order: 2,
      },
      {
        type: 'bar',
        label: 'Interest Paid',
        data: interestValues,
        yAxisID: 'payments',
        stack: 'payments',
        backgroundColor: 'rgba(220,38,38,0.72)',
        borderColor: 'rgba(185,28,28,0.22)',
        borderWidth: 1,
        borderRadius: 4,
        maxBarThickness: 18,
        order: 2,
      },
      {
        type: 'line',
        label: 'Remaining Balance',
        data: balanceValues,
        yAxisID: 'balance',
        borderColor: '#2563EB',
        borderWidth: 2.5,
        backgroundColor: 'rgba(37,99,235,0.12)',
        fill: false,
        tension: 0.32,
        pointBackgroundColor: pointColors,
        pointBorderColor: pointColors,
        pointRadius: pointRadii,
        pointBorderWidth: pointBorderWidths,
        pointHoverRadius: 7,
        order: 1,
      },
    ],
  };

  return {
    payment,
    debtServicePayment,
    storedMonthlyPayment,
    hasPaymentOverride: storedMonthlyPayment != null && Math.abs(storedMonthlyPayment - payment) >= 0.5,
    principalPaid,
    interestPaid: roundCents(interestPaid),
    remainingBalance: balanceNow,
    payoffDate, monthsRemaining: Math.max(0, n - monthsElapsed),
    startYear,
    chartData,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────
const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);
  const photoInputRef = useRef(null);

  const [property, setProperty] = useState(null);
  const [income, setIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [valuations, setValuations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showPhotoManager, setShowPhotoManager] = useState(false);
  const [metricSettings, setMetricSettings] = useState(DEFAULT_METRIC_SETTINGS);
  const [metricPreferenceLoaded, setMetricPreferenceLoaded] = useState(false);
  const [showMetricMethodology, setShowMetricMethodology] = useState(false);
  const [showContractRentEditor, setShowContractRentEditor] = useState(false);
  const [contractRentInput, setContractRentInput] = useState('');
  const [savingContractRent, setSavingContractRent] = useState(false);
  const [showRentcastApiKeyModal, setShowRentcastApiKeyModal] = useState(false);
  const [rentcastApiKeyInput, setRentcastApiKeyInput] = useState('');
  const [loadingRentcastApiKeyStatus, setLoadingRentcastApiKeyStatus] = useState(false);
  const [savingRentcastApiKey, setSavingRentcastApiKey] = useState(false);
  const [deletingRentcastApiKey, setDeletingRentcastApiKey] = useState(false);
  const [runningRentcastRefresh, setRunningRentcastRefresh] = useState(false);
  const [showRentcastApiKeyEditor, setShowRentcastApiKeyEditor] = useState(false);
  const [hasRentcastApiKey, setHasRentcastApiKey] = useState(false);
  const [rentcastApiKeyUpdatedAt, setRentcastApiKeyUpdatedAt] = useState(null);
  const [rentcastAutoRefreshEnabled, setRentcastAutoRefreshEnabled] = useState(false);
  const [rentcastRefreshFrequencyDays, setRentcastRefreshFrequencyDays] = useState('30');
  const [rentcastActivePropertyCount, setRentcastActivePropertyCount] = useState(0);
  const [rentcastRollingWindowDays, setRentcastRollingWindowDays] = useState(RENTCAST_USAGE_WINDOW_DAYS);
  const [rentcastCallsUsedInWindow, setRentcastCallsUsedInWindow] = useState(0);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [savingProperty, setSavingProperty] = useState(false);
  const [propertyForm, setPropertyForm] = useState(buildPropertyForm(null));
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [savingLoan, setSavingLoan] = useState(false);
  const [deletingLoan, setDeletingLoan] = useState(false);
  const [loanForm, setLoanForm] = useState(buildLoanForm(null));
  const [incomeExpenseRange, setIncomeExpenseRange] = useState('12');
  const [pmFeeAuditRange, setPmFeeAuditRange] = useState('12');
  const [sourceEvidence, setSourceEvidence] = useState(null);
  const [archivePreview, setArchivePreview] = useState(null);
  const [archivePhrase, setArchivePhrase] = useState('');
  const [archiveAcknowledged, setArchiveAcknowledged] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiving, setArchiving] = useState(false);

  const fetchPhotos = () => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('auth_token')}` };
    fetch(apiUrl(`/properties/${id}/photos`), { headers })
      .then(r => r.json()).then(d => setPhotos(Array.isArray(d) ? d : []));
  };

  useEffect(() => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('auth_token')}` };
    Promise.all([
      fetch(apiUrl(`/properties/${id}`), { headers }).then(r => r.json()),
      fetch(apiUrl(`/properties/${id}/income`), { headers }).then(r => r.json()),
      fetch(apiUrl(`/properties/${id}/expenses`), { headers }).then(r => r.json()),
      fetch(apiUrl(`/documents?property_id=${id}`), { headers }).then(r => r.json()),
      fetch(apiUrl(`/properties/${id}/photos`), { headers }).then(r => r.json()),
      fetch(apiUrl(`/properties/${id}/valuations`), { headers }).then(r => r.json()),
    ]).then(([prop, inc, exp, docs, ph, val]) => {
      setProperty(prop);
      setIncome(Array.isArray(inc) ? inc : []);
      setExpenses(Array.isArray(exp) ? exp : []);
      setDocuments(Array.isArray(docs) ? docs : []);
      setPhotos(Array.isArray(ph) ? ph : []);
      setValuations(Array.isArray(val) ? val : []);
    }).catch(() => toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load property', life: 4000 }))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('auth_token')}` };
    fetch(apiUrl(`/properties/${id}/source-evidence`), { headers })
      .then((response) => response.ok ? response.json() : null)
      .then(setSourceEvidence).catch(() => {});
  }, [id]);

  const openArchive = async () => {
    const response = await fetch(apiUrl(`/properties/${id}/archive-preview`), { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) { toast.current?.show({ severity: 'error', summary: 'Could not prepare archive', detail: data.error || 'Try again.', life: 4000 }); return; }
    setArchivePhrase(''); setArchiveAcknowledged(false); setArchiveReason(''); setArchivePreview(data);
  };

  const archiveProperty = async () => {
    setArchiving(true);
    try {
      const response = await fetch(apiUrl(`/properties/${id}/archive`), { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ confirmation_phrase: archivePhrase, acknowledge_retention: archiveAcknowledged, reason: archiveReason }) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Could not archive property.');
      navigate('/properties', { replace: true });
    } catch (error) { toast.current?.show({ severity: 'error', summary: 'Archive failed', detail: error.message, life: 4000 }); }
    finally { setArchiving(false); }
  };

  useEffect(() => {
    apiJson('/identity/preferences/property_detail.metric_settings')
      .then((preference) => { if (preference.value) setMetricSettings(normalizeMetricSettings(preference.value)); })
      .catch(() => undefined)
      .finally(() => setMetricPreferenceLoaded(true));
  }, []);

  useEffect(() => {
    if (!metricPreferenceLoaded) return;
    void apiJson('/identity/preferences/property_detail.metric_settings', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: metricSettings }),
    }).catch(() => undefined);
  }, [metricPreferenceLoaded, metricSettings]);

  useEffect(() => {
    setContractRentInput(property?.contract_rent != null ? String(property.contract_rent) : '');
    setShowContractRentEditor(false);
  }, [property?.id, property?.contract_rent]);

  useEffect(() => {
    setPropertyForm(buildPropertyForm(property));
    setShowPropertyModal(false);
  }, [property]);

  useEffect(() => {
    setLoanForm(buildLoanForm(property));
    setShowLoanModal(false);
  }, [property?.loan_id, property?.lender_name, property?.original_principal, property?.interest_rate, property?.loan_term_months, property?.loan_start_date, property?.monthly_payment, property?.loan_notes]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    const fd = new FormData();
    fd.append('photo', file);
    fd.append('is_primary', photos.length === 0 ? 'true' : 'false');
    try {
      const res = await fetch(apiUrl(`/properties/${id}/photos`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        body: fd,
      });
      if (res.ok) fetchPhotos();
      else toast.current?.show({ severity: 'error', summary: 'Upload Failed', detail: 'Could not upload photo', life: 4000 });
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Connection Error', detail: 'Unable to connect to server', life: 4000 });
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const handleDeletePhoto = async (photoId) => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('auth_token')}` };
    const res = await fetch(apiUrl(`/photos/${photoId}`), { method: 'DELETE', headers });
    if (res.ok) fetchPhotos();
    else toast.current?.show({ severity: 'error', summary: 'Delete Failed', detail: 'Could not delete photo', life: 4000 });
  };

  const handleSetPrimary = async (photoId) => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('auth_token')}` };
    const res = await fetch(apiUrl(`/photos/${photoId}/set-primary`), { method: 'PUT', headers });
    if (res.ok) fetchPhotos();
  };

  const handleSaveContractRent = async () => {
    const trimmed = contractRentInput.trim();
    if (!trimmed) {
      toast.current?.show({ severity: 'warn', summary: 'Missing Rent', detail: 'Enter the current contract rent amount.', life: 3000 });
      return;
    }
    const amount = Number.parseFloat(trimmed);
    if (!Number.isFinite(amount) || amount < 0) {
      toast.current?.show({ severity: 'warn', summary: 'Invalid Rent', detail: 'Contract rent must be a non-negative number.', life: 3000 });
      return;
    }

    setSavingContractRent(true);
    try {
      const res = await fetch(apiUrl(`/properties/${id}/contract-rent`), {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ contract_rent: amount }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Could not save contract rent');
      }
      setProperty((current) => current ? {
        ...current,
        contract_rent: data.contract_rent,
        contract_rent_updated_at: data.contract_rent_updated_at,
      } : current);
      setShowContractRentEditor(false);
      toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Contract rent updated.', life: 2500 });
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Save Failed', detail: error.message || 'Could not save contract rent', life: 4000 });
    } finally {
      setSavingContractRent(false);
    }
  };

  const handlePropertyFieldChange = (field, value) => {
    setPropertyForm((current) => ({ ...current, [field]: value }));
  };

  const handleSaveProperty = async () => {
    if (!propertyForm.address_line1.trim() || !propertyForm.city.trim() || !propertyForm.state.trim() || !propertyForm.zip.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Missing Address Fields', detail: 'Street, city, state, and ZIP are required.', life: 3500 });
      return;
    }
    if (!propertyForm.purchase_price || !propertyForm.purchase_date) {
      toast.current?.show({ severity: 'warn', summary: 'Missing Purchase Details', detail: 'Purchase price and purchase date are required.', life: 3500 });
      return;
    }

    setSavingProperty(true);
    try {
      const res = await fetch(apiUrl(`/properties/${id}`), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...propertyForm,
          state: propertyForm.state.trim().toUpperCase(),
        }),
      });
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Could not update property');
      }
      setProperty((current) => current ? { ...current, ...data } : current);
      setShowPropertyModal(false);
      toast.current?.show({ severity: 'success', summary: 'Property Updated', detail: 'Property information saved.', life: 2500 });
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Save Failed', detail: error.message || 'Could not update property', life: 4000 });
    } finally {
      setSavingProperty(false);
    }
  };

  const handleClearContractRent = async () => {
    setSavingContractRent(true);
    try {
      const res = await fetch(apiUrl(`/properties/${id}/contract-rent`), {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ contract_rent: null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Could not clear contract rent');
      }
      setProperty((current) => current ? {
        ...current,
        contract_rent: null,
        contract_rent_updated_at: null,
      } : current);
      setContractRentInput('');
      setShowContractRentEditor(false);
      toast.current?.show({ severity: 'success', summary: 'Cleared', detail: 'Rent analysis is using the latest ledger rent again.', life: 2500 });
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Clear Failed', detail: error.message || 'Could not clear contract rent', life: 4000 });
    } finally {
      setSavingContractRent(false);
    }
  };

  const applyRentcastApiKeyStatus = (data, fallbackFrequencyDays = 30) => {
    setHasRentcastApiKey(Boolean(data.has_api_key));
    setRentcastApiKeyUpdatedAt(data.updated_at || null);
    setRentcastAutoRefreshEnabled(Boolean(data.auto_refresh_enabled));
    setRentcastRefreshFrequencyDays(String(data.refresh_frequency_days ?? fallbackFrequencyDays));
    setRentcastActivePropertyCount(Number.parseInt(data.active_property_count, 10) || 0);
    setRentcastRollingWindowDays(Number.parseInt(data.rolling_window_days, 10) || RENTCAST_USAGE_WINDOW_DAYS);
    setRentcastCallsUsedInWindow(Number.parseInt(data.calls_used_in_window, 10) || 0);
  };

  const applyRentcastUsageStatus = (settings, fallbackFrequencyDays = 30) => {
    if (!settings) return;
    setRentcastAutoRefreshEnabled(Boolean(settings.auto_refresh_enabled));
    setRentcastRefreshFrequencyDays(String(settings.refresh_frequency_days ?? fallbackFrequencyDays));
    setRentcastActivePropertyCount(Number.parseInt(settings.active_property_count, 10) || 0);
    setRentcastRollingWindowDays(Number.parseInt(settings.rolling_window_days, 10) || RENTCAST_USAGE_WINDOW_DAYS);
    setRentcastCallsUsedInWindow(Number.parseInt(settings.calls_used_in_window, 10) || 0);
  };

  const loadRentcastApiKeyStatus = async () => {
    setLoadingRentcastApiKeyStatus(true);
    try {
      const res = await fetch(apiUrl('/user/rentcast-api-key'), {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      });
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Could not load API key status');
      }
      applyRentcastApiKeyStatus(data, 30);
      setShowRentcastApiKeyEditor(!Boolean(data.has_api_key));
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: error.message || 'Failed to load API key status', life: 4000 });
    } finally {
      setLoadingRentcastApiKeyStatus(false);
    }
  };

  const handleOpenRentcastApiKeyModal = () => {
    setRentcastApiKeyInput('');
    setShowRentcastApiKeyEditor(false);
    setShowRentcastApiKeyModal(true);
    void loadRentcastApiKeyStatus();
  };

  const handleCloseRentcastApiKeyModal = () => {
    if (savingRentcastApiKey || deletingRentcastApiKey) return;
    setRentcastApiKeyInput('');
    setShowRentcastApiKeyEditor(false);
    setShowRentcastApiKeyModal(false);
  };

  const handleEditRentcastApiKey = () => {
    setRentcastApiKeyInput('');
    setShowRentcastApiKeyEditor(true);
  };

  const handleCancelRentcastApiKeyEditor = () => {
    if (savingRentcastApiKey || deletingRentcastApiKey) return;
    setRentcastApiKeyInput('');
    if (hasRentcastApiKey) {
      setShowRentcastApiKeyEditor(false);
      return;
    }
    setShowRentcastApiKeyEditor(false);
    setShowRentcastApiKeyModal(false);
  };

  const handleToggleRentcastAutoRefresh = async (nextEnabled) => {
    const parsedFrequencyDays = Number.parseInt(rentcastRefreshFrequencyDays, 10);
    const safeFrequencyDays = Number.isFinite(parsedFrequencyDays) && parsedFrequencyDays >= 1 && parsedFrequencyDays <= 365
      ? parsedFrequencyDays
      : 30;

    setSavingRentcastApiKey(true);
    try {
      const res = await fetch(apiUrl('/user/rentcast-api-key'), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auto_refresh_enabled: nextEnabled,
          refresh_frequency_days: safeFrequencyDays,
        }),
      });
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Could not update automatic refresh');
      }
      applyRentcastApiKeyStatus(data, safeFrequencyDays);
      toast.current?.show({
        severity: 'success',
        summary: nextEnabled ? 'Automatic Refresh On' : 'Automatic Refresh Off',
        detail: nextEnabled ? 'RentCast automatic refresh is enabled for this account.' : 'RentCast automatic refresh is paused for this account.',
        life: 2200,
      });
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Update Failed', detail: error.message || 'Could not update automatic refresh', life: 4000 });
    } finally {
      setSavingRentcastApiKey(false);
    }
  };

  const handleSaveRentcastApiKey = async () => {
    const apiKey = rentcastApiKeyInput.trim();
    const parsedFrequencyDays = Number.parseInt(rentcastRefreshFrequencyDays, 10);
    if (!Number.isFinite(parsedFrequencyDays) || parsedFrequencyDays < 1 || parsedFrequencyDays > 365) {
      toast.current?.show({ severity: 'warn', summary: 'Invalid Frequency', detail: 'Refresh frequency must be between 1 and 365 days.', life: 3000 });
      return;
    }
    if (!hasRentcastApiKey && !apiKey) {
      toast.current?.show({ severity: 'warn', summary: 'Missing API Key', detail: 'Paste your personal RentCast API key to continue.', life: 3500 });
      return;
    }
    const nextAutoRefreshEnabled = hasRentcastApiKey ? rentcastAutoRefreshEnabled : Boolean(apiKey);

    setSavingRentcastApiKey(true);
    try {
      const res = await fetch(apiUrl('/user/rentcast-api-key'), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          api_key: apiKey,
          auto_refresh_enabled: nextAutoRefreshEnabled,
          refresh_frequency_days: parsedFrequencyDays,
        }),
      });
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Could not save API key');
      }
      applyRentcastApiKeyStatus(data, parsedFrequencyDays);
      setRentcastApiKeyInput('');
      setShowRentcastApiKeyEditor(false);
      toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Your personal RentCast settings were updated.', life: 2500 });
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Save Failed', detail: error.message || 'Could not save RentCast settings', life: 4000 });
    } finally {
      setSavingRentcastApiKey(false);
    }
  };

  const handleDeleteRentcastApiKey = async () => {
    const confirmed = window.confirm('Remove your saved personal RentCast API key?');
    if (!confirmed) return;

    setDeletingRentcastApiKey(true);
    try {
      const res = await fetch(apiUrl('/user/rentcast-api-key'), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      });
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Could not remove API key');
      }
      applyRentcastApiKeyStatus(data, 30);
      setRentcastApiKeyInput('');
      setShowRentcastApiKeyEditor(true);
      toast.current?.show({ severity: 'success', summary: 'Removed', detail: 'Your personal RentCast API key was removed and automatic refresh was turned off.', life: 2500 });
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Remove Failed', detail: error.message || 'Could not remove API key', life: 4000 });
    } finally {
      setDeletingRentcastApiKey(false);
    }
  };

  const handleRunRentcastNow = async () => {
    setRunningRentcastRefresh(true);
    try {
      const res = await fetch(apiUrl(`/properties/${id}/rentcast-refresh`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      });
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Could not run RentCast refresh');
      }

      if (data.property) {
        setProperty((current) => current ? { ...current, ...data.property } : current);
      }
      if (data.valuation) {
        setValuations((current) => {
          const rows = Array.isArray(current) ? current.filter((row) => row.id !== data.valuation.id) : [];
          return [...rows, data.valuation];
        });
      }
      applyRentcastUsageStatus(data.rentcast_settings, rentcastRefreshFrequencyDays);
      setHasRentcastApiKey(true);

      toast.current?.show({
        severity: data.warning ? 'warn' : 'success',
        summary: 'RentCast Refreshed',
        detail: data.warning ? `${data.message} ${data.warning}` : data.message || 'RentCast refresh completed.',
        life: data.warning ? 4500 : 3200,
      });
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Run Failed', detail: error.message || 'Could not run RentCast refresh', life: 4500 });
    } finally {
      setRunningRentcastRefresh(false);
    }
  };

  const handleLoanFieldChange = (field, value) => {
    setLoanForm((current) => ({ ...current, [field]: value }));
  };

  const handleSaveLoan = async () => {
    if (!loanForm.original_principal || !loanForm.interest_rate || !loanForm.loan_term_years || !loanForm.start_date) {
      toast.current?.show({ severity: 'warn', summary: 'Missing Loan Fields', detail: 'Principal, rate, term, and start date are required.', life: 3500 });
      return;
    }

    const loanTermYears = Number.parseFloat(loanForm.loan_term_years);
    if (!Number.isFinite(loanTermYears) || loanTermYears <= 0) {
      toast.current?.show({ severity: 'warn', summary: 'Invalid Loan Term', detail: 'Loan term must be a positive number of years.', life: 3500 });
      return;
    }

    const loanTermMonths = Math.round(loanTermYears * 12);
    if (loanTermMonths <= 0) {
      toast.current?.show({ severity: 'warn', summary: 'Invalid Loan Term', detail: 'Loan term must be at least one month.', life: 3500 });
      return;
    }

    const loanPayload = { ...loanForm, loan_term_months: loanTermMonths };
    delete loanPayload.loan_term_years;

    setSavingLoan(true);
    try {
      const res = await fetch(apiUrl(`/properties/${id}/loan`), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loanPayload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Could not save loan');
      }
      setProperty((current) => current ? { ...current, ...data } : current);
      setShowLoanModal(false);
      toast.current?.show({ severity: 'success', summary: 'Loan Updated', detail: 'Loan information saved.', life: 2500 });
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Save Failed', detail: error.message || 'Could not save loan', life: 4000 });
    } finally {
      setSavingLoan(false);
    }
  };

  const handleDeleteLoan = async () => {
    if (!property?.loan_id) return;
    const confirmed = window.confirm('Delete this loan information from the property?');
    if (!confirmed) return;

    setDeletingLoan(true);
    try {
      const res = await fetch(apiUrl(`/properties/${id}/loan`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Could not delete loan');
      }
      setProperty((current) => current ? {
        ...current,
        loan_id: null,
        lender_name: null,
        original_principal: null,
        interest_rate: null,
        loan_term_months: null,
        loan_start_date: null,
        monthly_payment: null,
        loan_notes: null,
      } : current);
      setLoanForm(buildLoanForm(null));
      setShowLoanModal(false);
      toast.current?.show({ severity: 'success', summary: 'Loan Deleted', detail: 'Loan information removed.', life: 2500 });
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Delete Failed', detail: error.message || 'Could not delete loan', life: 4000 });
    } finally {
      setDeletingLoan(false);
    }
  };

  if (loading) return <PageLoader label="Loading property" />;
  if (!property || property.error) return <div className="pd-error">Property not found.</div>;

  // ── Derived metrics ───────────────────────────────────────────────────────
  const dep = calcDepreciation(property);
  const amort = buildAmortization(property);
  const monthlyDebtService = property.monthly_payment != null
    ? parseFloat(property.monthly_payment) || 0
    : (amort ? amort.payment : 0);
  const metrics = calculatePropertyMetrics({
    property,
    income,
    expenses,
    monthlyDebtService,
    remainingBalance: amort ? amort.remainingBalance : null,
    settings: metricSettings,
  });
  const totalOwnerDistributions = metrics.totalOwnerDistributions;
  const appreciation = property.current_value ? parseFloat(property.current_value) - parseFloat(property.purchase_price) : null;
  const latestSummary = metrics.monthlySummaries.length > 0 ? metrics.monthlySummaries[metrics.monthlySummaries.length - 1] : null;
  const latestMonthIncome = latestSummary ? latestSummary.income : null;
  const latestMonthExpenses = latestSummary ? latestSummary.expenses : null;
  const latestMonthCashFlow = (latestMonthIncome != null && latestMonthExpenses != null)
    ? latestMonthIncome - latestMonthExpenses - monthlyDebtService
    : null;
  const latestMonthCashFlowTone = latestMonthCashFlow == null ? '' : (latestMonthCashFlow >= 0 ? 'green' : 'red');
  const trailing12Metrics = calculatePropertyMetrics({
    property,
    income,
    expenses,
    monthlyDebtService,
    remainingBalance: amort ? amort.remainingBalance : null,
    settings: { ...metricSettings, cashFlowMethod: 'trailing_12_calendar', returnMethod: 'purchase_price', includeDebtService: true },
  });
  const trailing12CashFlow = trailing12Metrics.monthlyCashFlow;
  const trailing12AnnualCashFlow = trailing12Metrics.annualCashFlow;
  const purchasePriceAmount = property.purchase_price ? parseFloat(property.purchase_price) : null;
  const yieldOnCostRate = trailing12AnnualCashFlow != null && purchasePriceAmount > 0
    ? (trailing12AnnualCashFlow / purchasePriceAmount) * 100
    : null;
  const trailing12Tone = trailing12CashFlow == null ? '' : (trailing12CashFlow >= 0 ? 'green' : 'red');
  const yieldOnCostTone = yieldOnCostRate == null ? '' : (yieldOnCostRate >= 0 ? 'green' : 'red');
  const rentTotalsByMonth = income.reduce((totals, row) => {
    if (String(row.category || '').toLowerCase() !== 'rent') return totals;
    const monthKey = String(row.period_month || '').slice(0, 7);
    if (!monthKey) return totals;
    totals[monthKey] = (totals[monthKey] || 0) + (parseFloat(row.amount) || 0);
    return totals;
  }, {});
  const latestLedgerRentMonth = Object.keys(rentTotalsByMonth).sort().slice(-1)[0] || null;
  const latestLedgerRentAmount = latestLedgerRentMonth ? rentTotalsByMonth[latestLedgerRentMonth] : null;
  const latestLedgerRentMonthLabel = latestLedgerRentMonth ? formatMonthLabel(latestLedgerRentMonth) : null;
  const contractRentAmount = property.contract_rent != null ? (parseFloat(property.contract_rent) || 0) : null;
  const displayedRentAmount = contractRentAmount != null ? contractRentAmount : latestLedgerRentAmount;
  const contractRentUpdatedLabel = property.contract_rent_updated_at
    ? new Date(property.contract_rent_updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  const rentcastApiKeyUpdatedLabel = rentcastApiKeyUpdatedAt
    ? new Date(rentcastApiKeyUpdatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  const parsedRentcastRefreshFrequencyDays = Number.parseInt(rentcastRefreshFrequencyDays, 10);
  const rentcastEffectiveFrequencyDays = Number.isFinite(parsedRentcastRefreshFrequencyDays) && parsedRentcastRefreshFrequencyDays > 0
    ? parsedRentcastRefreshFrequencyDays
    : 30;
  const rentcastEstimatedCalls = computeRentcastEstimatedCalls(
    rentcastActivePropertyCount,
    rentcastEffectiveFrequencyDays,
    rentcastRollingWindowDays,
  );
  const rentcastUsageSummary = rentcastActivePropertyCount > 0
    ? `Estimated usage: up to ${fmtNum(rentcastEstimatedCalls)} call${rentcastEstimatedCalls === 1 ? '' : 's'} in a rolling ${rentcastRollingWindowDays}-day window for ${fmtNum(rentcastActivePropertyCount)} active propert${rentcastActivePropertyCount === 1 ? 'y' : 'ies'}.`
    : 'No active properties yet. Usage starts once this account has active properties.';
  const rentcastRecentUsageSummary = `Recent usage: ${fmtNum(rentcastCallsUsedInWindow)} calls in the last ${rentcastRollingWindowDays} days.`;
  const latestValuation = valuations.length > 0 ? valuations[valuations.length - 1] : null;
  const marketRent = property.current_rent_estimate ? parseFloat(property.current_rent_estimate) : toFiniteNumber(latestValuation?.estimated_rent);
  const marketRentLow = toFiniteNumber(latestValuation?.rent_low);
  const marketRentHigh = toFiniteNumber(latestValuation?.rent_high);
  const marketRentRangeLabel = marketRentLow != null && marketRentHigh != null
    ? `${fmt(marketRentLow)} - ${fmt(marketRentHigh)}/mo`
    : marketRentLow != null
      ? `Low ${fmt(marketRentLow)}/mo`
      : marketRentHigh != null
        ? `High ${fmt(marketRentHigh)}/mo`
        : null;
  const marketRentUpdatedAt = property.rent_updated_at || latestValuation?.fetched_at || property.valuation_updated_at;
  const marketRentUpdatedLabel = marketRentUpdatedAt ? new Date(marketRentUpdatedAt).toLocaleDateString() : null;
  const rentVariance = marketRent != null && displayedRentAmount != null ? displayedRentAmount - marketRent : null;
  const rentPct = marketRent != null && displayedRentAmount != null && marketRent > 0 ? (displayedRentAmount / marketRent) * 100 : null;
  const grossYield = property.current_value && marketRent ? (marketRent * 12 / parseFloat(property.current_value)) * 100 : null;

  const chartOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } };
  const barOpts = { ...chartOpts, scales: { x: { stacked: false }, y: { beginAtZero: true, ticks: { callback: v => `$${v.toLocaleString()}` } } } };
  const expectedIncomeMonthly = contractRentAmount ?? latestLedgerRentAmount ?? marketRent ?? null;
  const expectedIncomeSource = contractRentAmount != null
    ? 'contract rent'
    : latestLedgerRentAmount != null
      ? 'latest ledger rent'
      : marketRent != null
        ? 'market rent'
        : null;
  const incomeExpenseAllMonths = metrics.monthlySummaries;
  const latestStatementMonth = documents
    .filter((doc) => doc?.doc_type === 'manager_report' && doc?.period_month)
    .map((doc) => monthKeyFromValue(doc.period_month))
    .filter(Boolean)
    .sort()
    .slice(-1)[0] || null;
  const incomeExpenseRangeCount = Number(incomeExpenseRange);
  const latestIncomeExpenseMonth = incomeExpenseAllMonths
    .map((entry) => entry.month)
    .filter(Boolean)
    .sort()
    .slice(-1)[0] || null;
  const incomeExpenseWindowEndMonth = latestStatementMonth
    || latestIncomeExpenseMonth
    || (expectedIncomeMonthly != null ? monthKeyFromDate(new Date()) : null);
  const incomeExpenseMonthKeys = buildMonthWindow(incomeExpenseWindowEndMonth, incomeExpenseRangeCount);
  const incomeExpenseMonthMap = new Map(incomeExpenseAllMonths.map((entry) => [entry.month, entry]));
  const incomeExpenseMonths = incomeExpenseMonthKeys.map((month) => {
    const entry = incomeExpenseMonthMap.get(month);
    return {
      month,
      income: entry?.income ?? 0,
      expenses: entry?.expenses ?? 0,
    };
  });
  const incomeExpenseWindowLabel = incomeExpenseMonths.length > 0
    ? `${formatMonthLabel(incomeExpenseMonths[0].month)} through ${formatMonthLabel(incomeExpenseMonths[incomeExpenseMonths.length - 1].month)}`
    : null;
  const incomeExpenseDatasets = [
    ...(expectedIncomeMonthly != null ? [{
      label: 'Expected Income',
      data: incomeExpenseMonths.map(() => expectedIncomeMonthly),
      backgroundColor: 'rgba(37, 99, 235, 0.86)',
      hoverBackgroundColor: '#1D4ED8',
      borderColor: 'rgba(30, 64, 175, 0.25)',
      borderWidth: 1,
      borderRadius: { topLeft: 3, topRight: 3 },
      borderSkipped: 'bottom',
      maxBarThickness: 16,
      categoryPercentage: 0.72,
      barPercentage: 0.82,
    }] : []),
    {
      label: 'Actual Income',
      data: incomeExpenseMonths.map((entry) => entry.income),
      backgroundColor: 'rgba(5, 150, 105, 0.88)',
      hoverBackgroundColor: '#047857',
      borderColor: 'rgba(4, 120, 87, 0.22)',
      borderWidth: 1,
      borderRadius: { topLeft: 3, topRight: 3 },
      borderSkipped: 'bottom',
      maxBarThickness: 16,
      categoryPercentage: 0.72,
      barPercentage: 0.82,
    },
    {
      label: 'Operating Expenses',
      data: incomeExpenseMonths.map((entry) => entry.expenses),
      backgroundColor: 'rgba(220, 38, 38, 0.84)',
      hoverBackgroundColor: '#B91C1C',
      borderColor: 'rgba(153, 27, 27, 0.2)',
      borderWidth: 1,
      borderRadius: { topLeft: 3, topRight: 3 },
      borderSkipped: 'bottom',
      maxBarThickness: 16,
      categoryPercentage: 0.72,
      barPercentage: 0.82,
    },
  ];
  const incomeExpenseChart = {
    labels: incomeExpenseMonths.map((entry) => formatMonthLabel(entry.month)),
    datasets: incomeExpenseDatasets,
  };
  let priorCollectionRate = null;
  const incomeExpenseAllRows = incomeExpenseMonths.map((entry) => {
    const expectedIncome = expectedIncomeMonthly;
    const actualIncome = entry.income;
    const operatingExpenses = entry.expenses;
    const netOperatingIncome = actualIncome - operatingExpenses;
    const collectionRate = expectedIncome != null && expectedIncome > 0
      ? (actualIncome / expectedIncome) * 100
      : null;
    const collectionBpsChange = collectionRate != null && priorCollectionRate != null
      ? Math.round((collectionRate - priorCollectionRate) * 100)
      : null;

    if (collectionRate != null) {
      priorCollectionRate = collectionRate;
    }

    return {
      month: entry.month,
      monthLabel: formatMonthLabel(entry.month),
      expectedIncome,
      actualIncome,
      operatingExpenses,
      netOperatingIncome,
      incomeVariance: expectedIncome != null ? actualIncome - expectedIncome : null,
      collectionRate,
      collectionBpsChange,
    };
  });
  const incomeExpenseTableRows = incomeExpenseAllRows
    .reverse();
  const incomeExpenseOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          color: '#475569',
          font: { size: 12, weight: '600' },
          padding: 16,
          usePointStyle: false,
        },
      },
      tooltip: {
        backgroundColor: '#0F172A',
        borderColor: 'rgba(255, 255, 255, 0.12)',
        borderWidth: 1,
        cornerRadius: 10,
        displayColors: true,
        padding: 10,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.raw)}`,
        },
      },
    },
    layout: { padding: { top: 4, right: 8, bottom: 0, left: 4 } },
    scales: {
      x: {
        stacked: false,
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: '#64748B',
          font: { size: 11, weight: '600' },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: Number(incomeExpenseRange),
        },
      },
      y: {
        beginAtZero: true,
        border: { display: false },
        ticks: {
          color: '#64748B',
          font: { size: 11, weight: '600' },
          callback: (value) => `$${Number(value).toLocaleString()}`,
        },
        grid: { color: 'rgba(148, 163, 184, 0.16)' },
      },
    },
  };
  const pmFeeExpenseEntries = expenses.reduce((entries, row) => {
    if (row.category === 'owner_distribution') return entries;
    const month = monthKeyFromValue(row.expense_date);
    const feeType = classifyPmFeeExpense(row);
    if (!month || !feeType) return entries;
    entries.push({
      month,
      feeType,
      amount: parseFloat(row.amount) || 0,
      vendor: row.vendor || '',
      description: row.description || '',
      category: row.category || '',
    });
    return entries;
  }, []);
  const pmFeeMonthsWithActivity = Array.from(new Set([
    ...Object.keys(rentTotalsByMonth),
    ...pmFeeExpenseEntries.map((entry) => entry.month),
    latestStatementMonth,
    latestIncomeExpenseMonth,
  ].filter(Boolean))).sort();
  const pmFeeAuditRangeCount = Number(pmFeeAuditRange);
  const pmFeeWindowEndMonth = pmFeeMonthsWithActivity.slice(-1)[0] || null;
  const pmFeeAuditMonthKeys = buildMonthWindow(pmFeeWindowEndMonth, pmFeeAuditRangeCount);
  const pmFeeEntriesByMonth = pmFeeExpenseEntries.reduce((grouped, entry) => {
    if (!grouped[entry.month]) grouped[entry.month] = [];
    grouped[entry.month].push(entry);
    return grouped;
  }, {});
  const pmFeeAuditRows = pmFeeAuditMonthKeys.map((month) => {
    const monthEntries = pmFeeEntriesByMonth[month] || [];
    const managementEntries = monthEntries.filter((entry) => entry.feeType === 'management');
    const totals = monthEntries.reduce((sum, entry) => {
      sum[entry.feeType] = roundMoney((sum[entry.feeType] || 0) + entry.amount);
      return sum;
    }, {});
    const expectedManagementFee = roundMoney((rentTotalsByMonth[month] || 0) * MANAGEMENT_FEE_RATE);
    const actualManagementFee = roundMoney(totals.management || 0);
    const evictionFees = roundMoney(totals.eviction || 0);
    const renewalFees = roundMoney(totals.renewal || 0);
    const advertisingFees = roundMoney(totals.advertising || 0);
    const otherFees = roundMoney(totals.other || 0);
    const totalPmFees = roundMoney(actualManagementFee + evictionFees + renewalFees + advertisingFees + otherFees);
    return {
      month,
      monthLabel: formatMonthLabel(month),
      expectedManagementFee,
      actualManagementFee,
      evictionFees,
      renewalFees,
      advertisingFees,
      otherFees,
      totalPmFees,
      status: buildPmFeeAuditStatus(expectedManagementFee, actualManagementFee, managementEntries),
    };
  }).reverse();
  const pmFeeAuditWindowLabel = pmFeeAuditRows.length > 0
    ? `${formatMonthLabel(pmFeeAuditRows[pmFeeAuditRows.length - 1].month)} through ${formatMonthLabel(pmFeeAuditRows[0].month)}`
    : null;
  const operatingExpenseRows = expenses.filter((row) => row.category !== 'owner_distribution');
  const expenseTrendMonths = Array.from(new Set([
    ...metrics.monthlySummaries.map((entry) => entry.month),
    ...operatingExpenseRows.map((row) => String(row.expense_date || '').slice(0, 7)).filter(Boolean),
  ])).sort().slice(-6);
  const expenseTrendCategoryTotals = {};
  const expenseTrendByMonthCategory = {};
  operatingExpenseRows.forEach((row) => {
    const month = String(row.expense_date || '').slice(0, 7);
    if (!month || !expenseTrendMonths.includes(month)) return;
    const category = row.category || 'other';
    const amount = parseFloat(row.amount) || 0;
    expenseTrendCategoryTotals[category] = (expenseTrendCategoryTotals[category] || 0) + amount;
    expenseTrendByMonthCategory[`${month}:${category}`] = (expenseTrendByMonthCategory[`${month}:${category}`] || 0) + amount;
  });
  const rankedExpenseCategories = Object.entries(expenseTrendCategoryTotals).sort((left, right) => right[1] - left[1]);
  const featuredExpenseCategories = rankedExpenseCategories.slice(0, 5).map(([category]) => category);
  const overflowExpenseCategories = rankedExpenseCategories.slice(5).map(([category]) => category);
  const expenseTrendDatasets = featuredExpenseCategories.map((category, index) => ({
    label: formatCategoryLabel(category),
    data: expenseTrendMonths.map((month) => expenseTrendByMonthCategory[`${month}:${category}`] || 0),
    backgroundColor: EXPENSE_TREND_COLORS[index % EXPENSE_TREND_COLORS.length],
    borderRadius: 6,
    borderSkipped: false,
    stack: 'expense-categories',
  }));
  if (overflowExpenseCategories.length > 0) {
    expenseTrendDatasets.push({
      label: 'Other',
      data: expenseTrendMonths.map((month) => overflowExpenseCategories.reduce((sum, category) => (
        sum + (expenseTrendByMonthCategory[`${month}:${category}`] || 0)
      ), 0)),
      backgroundColor: EXPENSE_TREND_COLORS[5],
      borderRadius: 6,
      borderSkipped: false,
      stack: 'expense-categories',
    });
  }
  const expenseTrendChart = {
    labels: expenseTrendMonths.map((month) => formatMonthLabel(month)),
    datasets: expenseTrendDatasets,
  };
  const expenseTrendOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12, usePointStyle: true, pointStyle: 'rectRounded' } },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.raw)}`,
        },
      },
    },
    scales: {
      x: { stacked: true, grid: { display: false } },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: { callback: (value) => `$${Number(value).toLocaleString()}` },
      },
    },
  };
  const rentBenchmarkItems = [
    latestLedgerRentAmount != null ? {
      key: 'collected',
      label: 'Collected',
      value: latestLedgerRentAmount,
      sublabel: latestLedgerRentMonthLabel ? `Latest ledger month · ${latestLedgerRentMonthLabel}` : 'Latest ledger month',
    } : null,
    contractRentAmount != null ? {
      key: 'contract',
      label: 'Contract',
      value: contractRentAmount,
      sublabel: contractRentUpdatedLabel ? `Saved ${contractRentUpdatedLabel}` : 'Current contract rent',
    } : null,
    marketRent != null ? {
      key: 'market',
      label: 'Market',
      value: marketRent,
      sublabel: marketRentUpdatedAt ? `Updated ${new Date(marketRentUpdatedAt).toLocaleDateString()}` : 'Rentcast estimate',
    } : null,
  ].filter(Boolean);
  const rentBenchmarkChart = {
    labels: rentBenchmarkItems.map((item) => item.label),
    datasets: [{
      label: 'Monthly Rent',
      data: rentBenchmarkItems.map((item) => item.value),
      backgroundColor: rentBenchmarkItems.map((item) => RENT_BENCHMARK_COLORS[item.key]),
      borderRadius: 10,
      borderSkipped: false,
      barThickness: 18,
    }],
  };
  const rentBenchmarkOpts = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.label}: ${fmt(ctx.raw)}/mo`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: 'rgba(148, 163, 184, 0.18)' },
        ticks: { callback: (value) => `$${Number(value).toLocaleString()}` },
      },
      y: {
        grid: { display: false },
        ticks: { color: '#475569', font: { weight: '700' } },
      },
    },
  };
  const collectedVsContractDelta = latestLedgerRentAmount != null && contractRentAmount != null
    ? latestLedgerRentAmount - contractRentAmount
    : null;
  const contractVsMarketDelta = contractRentAmount != null && marketRent != null
    ? contractRentAmount - marketRent
    : null;
  const primaryPhoto = photos.find(p => p.is_primary) || photos[0];
  const propertyTypeLabel = PROPERTY_TYPE_OPTIONS.find((option) => option.value === property.property_type)?.label
    || String(property.property_type || '').replaceAll('_', ' ');
  const moneyBody = (field, tone) => (row) => (
    <span className={`pd-money-cell ${tone || ''}`.trim()}>
      {fmt(row[field])}
    </span>
  );
  const varianceBody = (row) => {
    const value = row.incomeVariance;
    if (value == null) return <span className="pd-muted-cell">—</span>;
    return (
      <span className={`pd-money-cell ${value >= 0 ? 'green' : 'red'}`}>
        {value >= 0 ? '+' : ''}{fmt(value)}
      </span>
    );
  };
  const collectionRateBody = (row) => (
    <span className={`pd-rate-pill ${row.collectionRate == null ? '' : row.collectionRate >= 100 ? 'pd-rate-pill--good' : 'pd-rate-pill--warn'}`}>
      {formatPct(row.collectionRate)}
    </span>
  );
  const bpsBody = (row) => {
    const value = row.collectionBpsChange;
    if (value == null) return <span className="pd-muted-cell">—</span>;
    const direction = value > 0 ? 'up' : value < 0 ? 'down' : 'flat';
    return (
      <span className={`pd-bps-pill pd-bps-pill--${direction}`}>
        {value > 0 ? '↑ +' : value < 0 ? '↓ -' : ''}{value === 0 ? '0' : Math.abs(value)} bps
      </span>
    );
  };

  return (
    <div className="pd-page">
      <Toast ref={toast} position="top-right" />
      <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />

      {/* ── Hero: photo + info side by side ── */}
      <div className="pd-hero">
        <div className="pd-hero-photo" onClick={() => photoInputRef.current.click()}>
          {primaryPhoto ? (
            <>
              <img src={primaryPhoto.url} alt="Property" />
              <div className="pd-hero-photo-overlay">
                <i className="pi pi-camera" />
                <span>{uploadingPhoto ? 'Uploading...' : `${photos.length} photo${photos.length !== 1 ? 's' : ''} · add`}</span>
                {photos.length > 0 && (
                  <button className="pd-manage-photos-btn" onClick={e => { e.stopPropagation(); setShowPhotoManager(true); }}>
                    <i className="pi pi-images" /> Manage
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="pd-hero-photo-empty">
              <i className="pi pi-image" />
              <span>{uploadingPhoto ? 'Uploading...' : 'Add a photo'}</span>
            </div>
          )}
        </div>

        {/* ── Photo manager modal ── */}
        {showPhotoManager && (
          <div className="pd-photo-modal-backdrop" onClick={() => setShowPhotoManager(false)}>
            <div className="pd-photo-modal" onClick={e => e.stopPropagation()}>
              <div className="pd-photo-modal-header">
                <span>Manage Photos ({photos.length})</span>
                <button className="pd-photo-modal-close" onClick={() => setShowPhotoManager(false)}>
                  <i className="pi pi-times" />
                </button>
              </div>
              <div className="pd-photo-grid">
                {photos.map(p => (
                  <div key={p.id} className={`pd-photo-thumb ${p.is_primary ? 'pd-photo-thumb--primary' : ''}`}>
                    <img src={p.url} alt="" />
                    {p.is_primary && <span className="pd-photo-primary-badge">Primary</span>}
                    <div className="pd-photo-thumb-actions">
                      {!p.is_primary && (
                        <button onClick={() => handleSetPrimary(p.id)} title="Set as primary">
                          <i className="pi pi-star" />
                        </button>
                      )}
                      <button className="pd-photo-del-btn" onClick={() => handleDeletePhoto(p.id)} title="Delete">
                        <i className="pi pi-trash" />
                      </button>
                    </div>
                  </div>
                ))}
                <div className="pd-photo-thumb pd-photo-thumb--add" onClick={() => { setShowPhotoManager(false); photoInputRef.current.click(); }}>
                  <i className="pi pi-plus" />
                  <span>Add photo</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="pd-hero-info">
          <div className="pd-hero-topbar">
            <button className="pd-back" style={{ margin: 0 }} onClick={() => navigate('/properties')}>
              <i className="pi pi-arrow-left" /> Properties
            </button>
            <div className="pd-hero-actions">
              <button className="pd-ledger-btn pd-ledger-btn--secondary" onClick={() => setShowPropertyModal(true)}>
                <i className="pi pi-pencil" /> Edit Property
              </button>
              <button className="pd-ledger-btn" onClick={() => setShowLoanModal(true)}>
                <i className="pi pi-wallet" /> Loan Details
              </button>
              <button className="pd-ledger-btn pd-archive-btn" onClick={openArchive}>
                <i className="pi pi-archive" /> Archive
              </button>
              {/* <button className="pd-ledger-btn" onClick={() => navigate(`/properties/${id}/statements`)}>
                <i className="pi pi-file-pdf" /> Statements
              </button> */}
            </div>
          </div>
          <div className="pd-hero-title-row">
            <h1 className="pd-title">{property.nickname || property.address_line1}</h1>
            <span className="pd-badge">{propertyTypeLabel}</span>
          </div>
          <p className="pd-address">
            {property.address_line1}{property.address_line2 ? `, ${property.address_line2}` : ''}, {property.city}, {property.state} {property.zip}
          </p>
          <div className="pd-meta">
            {property.bedrooms != null && <span><i className="pi pi-home" /> {property.bedrooms} bd</span>}
            {property.bathrooms != null && <span>{property.bathrooms} ba</span>}
            {property.square_feet != null && <span>{fmtNum(property.square_feet)} sqft</span>}
            {property.year_built != null && <span>Built {property.year_built}</span>}
          </div>

          {/* Mini KPIs inside hero — 2 rows of 4 */}
          <div className="pd-hero-stats">
            <div className="pd-hero-stats-row">
              <div className="pd-hero-stat">
                <span className="pd-hero-stat-label">Purchase Price</span>
                <span className="pd-hero-stat-value">{fmt(property.purchase_price)}</span>
              </div>
              <div className="pd-hero-stat">
                <span className="pd-hero-stat-label">Current Value</span>
                <span className="pd-hero-stat-value">{property.current_value ? fmt(property.current_value) : '—'}</span>
                {appreciation != null && <span className={`pd-hero-stat-sub ${appreciation >= 0 ? 'green' : 'red'}`}>{appreciation >= 0 ? '+' : ''}{fmt(appreciation)}</span>}
              </div>
              <div className="pd-hero-stat">
                <span className="pd-hero-stat-label">12M Cash Flow</span>
                <span className={`pd-hero-stat-value ${yieldOnCostTone}`}>{fmt(trailing12AnnualCashFlow)}</span>
                <span className="pd-hero-stat-sub">last 12 months</span>
              </div>
              <div className="pd-hero-stat">
                <span className="pd-hero-stat-label">12M Avg Cash Flow</span>
                <span className={`pd-hero-stat-value ${trailing12Tone}`}>{fmt(trailing12CashFlow)}<span className="pd-hero-stat-unit">/mo</span></span>
                <span className="pd-hero-stat-sub">
                  12 month avg
                </span>
              </div>
            </div>
            <div className="pd-hero-stats-row">
              <div className="pd-hero-stat">
                <span className="pd-hero-stat-label">Latest Income</span>
                <span className="pd-hero-stat-value green">{fmt(latestMonthIncome)}<span className="pd-hero-stat-unit">/mo</span></span>
                <span className="pd-hero-stat-sub">{metrics.latestMonthLabel || '—'}</span>
              </div>
              <div className="pd-hero-stat">
                <span className="pd-hero-stat-label">Latest Expenses</span>
                <span className="pd-hero-stat-value red">{fmt(latestMonthExpenses)}<span className="pd-hero-stat-unit">/mo</span></span>
                <span className="pd-hero-stat-sub">{metrics.latestMonthLabel || '—'}</span>
              </div>
              <div className="pd-hero-stat">
                <span className="pd-hero-stat-label">Debt Service</span>
                <span className={`pd-hero-stat-value${monthlyDebtService ? ' red' : ''}`}>{monthlyDebtService ? fmt(monthlyDebtService) : '$0'}<span className="pd-hero-stat-unit">/mo</span></span>
                <span className="pd-hero-stat-sub">mortgage</span>
              </div>
              <div className="pd-hero-stat">
                <span className="pd-hero-stat-label">Latest Cash Flow</span>
                <span className={`pd-hero-stat-value ${latestMonthCashFlowTone}`}>{fmt(latestMonthCashFlow)}<span className="pd-hero-stat-unit">/mo</span></span>
                <span className="pd-hero-stat-sub">income − exp − mortgage</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {sourceEvidence && <section className="pd-source-evidence pd-card">
        <div><span className="pd-source-eyebrow">Acquisition evidence</span><h2>Original lead and API record</h2><p>The complete source JSON and enrichment responses remain linked to this property.</p></div>
        <details><summary>View original listing JSON</summary><pre>{JSON.stringify(sourceEvidence.raw_source_record, null, 2)}</pre></details>
        {(sourceEvidence.enrichments || []).map((item) => <details key={item.id}><summary>{item.provider} · {item.endpoint}</summary><pre>{JSON.stringify(item, null, 2)}</pre></details>)}
      </section>}

      {archivePreview && <div className="pd-property-modal-backdrop" onClick={() => !archiving && setArchivePreview(null)}>
        <div className="pd-property-modal pd-archive-modal" onClick={(event) => event.stopPropagation()}>
          <div className="pd-property-modal-header"><div><div className="pd-property-modal-eyebrow">Safe removal</div><h3>Archive property</h3></div><button type="button" className="pd-property-modal-close" disabled={archiving} onClick={() => setArchivePreview(null)}><i className="pi pi-times" /></button></div>
          <p>This hides the property from the active portfolio. It does not delete its records or evidence.</p>
          <ul>{Object.entries(archivePreview.retained_records || {}).map(([name, count]) => <li key={name}>{Number(count).toLocaleString()} {name.replaceAll('_', ' ')} retained</li>)}</ul>
          <label><span>Reason (optional)</span><textarea rows="3" value={archiveReason} onChange={(event) => setArchiveReason(event.target.value)} /></label>
          <label><span>Type exactly: <strong>{archivePreview.confirmation_phrase}</strong></span><input value={archivePhrase} onChange={(event) => setArchivePhrase(event.target.value)} /></label>
          <label className="pd-archive-check"><input type="checkbox" checked={archiveAcknowledged} onChange={(event) => setArchiveAcknowledged(event.target.checked)} /> I understand all data will be retained and the property will leave the active view.</label>
          <div className="pd-property-modal-actions"><button type="button" disabled={archiving} onClick={() => setArchivePreview(null)}>Cancel</button><button type="button" className="pd-archive-confirm" disabled={archiving || !archiveAcknowledged || archivePhrase !== archivePreview.confirmation_phrase} onClick={archiveProperty}>{archiving ? 'Archiving…' : 'Archive property'}</button></div>
        </div>
      </div>}

      {showPropertyModal && (
        <div className="pd-property-modal-backdrop" onClick={() => setShowPropertyModal(false)}>
          <div className="pd-property-modal" onClick={(event) => event.stopPropagation()}>
            <div className="pd-property-modal-header">
              <div>
                <div className="pd-property-modal-eyebrow">Property Profile</div>
                <h3>Edit property details</h3>
              </div>
              <button type="button" className="pd-property-modal-close" onClick={() => setShowPropertyModal(false)}>
                <i className="pi pi-times" />
              </button>
            </div>

            <div className="pd-property-modal-grid">
              <label>
                <span>Nickname</span>
                <input
                  type="text"
                  value={propertyForm.nickname}
                  onChange={(event) => handlePropertyFieldChange('nickname', event.target.value)}
                  placeholder="e.g. Shick"
                />
              </label>
              <label>
                <span>Property Type</span>
                <select
                  value={propertyForm.property_type}
                  onChange={(event) => handlePropertyFieldChange('property_type', event.target.value)}
                >
                  {PROPERTY_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>

              <label className="pd-property-modal-field--full">
                <span>Street Address</span>
                <input
                  type="text"
                  value={propertyForm.address_line1}
                  onChange={(event) => handlePropertyFieldChange('address_line1', event.target.value)}
                  placeholder="123 Main St"
                />
              </label>
              <label className="pd-property-modal-field--full">
                <span>Address Line 2</span>
                <input
                  type="text"
                  value={propertyForm.address_line2}
                  onChange={(event) => handlePropertyFieldChange('address_line2', event.target.value)}
                  placeholder="Unit, suite, or apartment"
                />
              </label>

              <label>
                <span>City</span>
                <input
                  type="text"
                  value={propertyForm.city}
                  onChange={(event) => handlePropertyFieldChange('city', event.target.value)}
                />
              </label>
              <label>
                <span>State</span>
                <input
                  type="text"
                  value={propertyForm.state}
                  maxLength="2"
                  onChange={(event) => handlePropertyFieldChange('state', event.target.value.toUpperCase())}
                  placeholder="IN"
                />
              </label>
              <label>
                <span>ZIP</span>
                <input
                  type="text"
                  value={propertyForm.zip}
                  onChange={(event) => handlePropertyFieldChange('zip', event.target.value)}
                  placeholder="46218"
                />
              </label>

              <label>
                <span>Bedrooms</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={propertyForm.bedrooms}
                  onChange={(event) => handlePropertyFieldChange('bedrooms', event.target.value)}
                />
              </label>
              <label>
                <span>Bathrooms</span>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={propertyForm.bathrooms}
                  onChange={(event) => handlePropertyFieldChange('bathrooms', event.target.value)}
                />
              </label>
              <label>
                <span>Sq Feet</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={propertyForm.square_feet}
                  onChange={(event) => handlePropertyFieldChange('square_feet', event.target.value)}
                />
              </label>
              <label>
                <span>Year Built</span>
                <input
                  type="number"
                  min="1800"
                  max="2100"
                  step="1"
                  value={propertyForm.year_built}
                  onChange={(event) => handlePropertyFieldChange('year_built', event.target.value)}
                />
              </label>

              <label>
                <span>Purchase Price</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={propertyForm.purchase_price}
                  onChange={(event) => handlePropertyFieldChange('purchase_price', event.target.value)}
                  placeholder="0.00"
                />
              </label>
              <label>
                <span>Asking Price</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={propertyForm.asking_price}
                  onChange={(event) => handlePropertyFieldChange('asking_price', event.target.value)}
                  placeholder="Defaults to purchase price"
                />
              </label>
              <label>
                <span>Purchase Date</span>
                <input
                  type="date"
                  value={propertyForm.purchase_date}
                  onChange={(event) => handlePropertyFieldChange('purchase_date', event.target.value)}
                />
              </label>
              <label>
                <span>Current Value</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={propertyForm.current_value}
                  onChange={(event) => handlePropertyFieldChange('current_value', event.target.value)}
                  placeholder="Optional"
                />
              </label>

              <label className="pd-property-modal-field--full">
                <span>Notes</span>
                <textarea
                  rows="4"
                  value={propertyForm.notes}
                  onChange={(event) => handlePropertyFieldChange('notes', event.target.value)}
                  placeholder="Property notes, access details, utility notes, lease notes, etc."
                />
              </label>
            </div>

            <div className="pd-property-modal-actions">
              <button
                type="button"
                className="pd-property-modal-btn pd-property-modal-btn--secondary"
                onClick={() => {
                  setPropertyForm(buildPropertyForm(property));
                  setShowPropertyModal(false);
                }}
                disabled={savingProperty}
              >
                Cancel
              </button>
              <button
                type="button"
                className="pd-property-modal-btn"
                onClick={handleSaveProperty}
                disabled={savingProperty}
              >
                {savingProperty ? 'Saving...' : 'Save Property'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLoanModal && (
        <div className="pd-loan-modal-backdrop" onClick={() => setShowLoanModal(false)}>
          <div className="pd-loan-modal" onClick={(event) => event.stopPropagation()}>
            <div className="pd-loan-modal-header">
              <div>
                <div className="pd-loan-modal-eyebrow">Active Loan</div>
                <h3>{property.loan_id ? 'Update loan details' : 'Add loan details'}</h3>
              </div>
              <button type="button" className="pd-loan-modal-close" onClick={() => setShowLoanModal(false)}>
                <i className="pi pi-times" />
              </button>
            </div>

            <div className="pd-loan-modal-grid">
              <label>
                <span>Lender Name</span>
                <input
                  type="text"
                  value={loanForm.lender_name}
                  onChange={(event) => handleLoanFieldChange('lender_name', event.target.value)}
                  placeholder="e.g. Chase"
                />
              </label>
              <label>
                <span>Start Date</span>
                <input
                  type="date"
                  value={loanForm.start_date}
                  onChange={(event) => handleLoanFieldChange('start_date', event.target.value)}
                />
              </label>
              <label>
                <span>Original Principal</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={loanForm.original_principal}
                  onChange={(event) => handleLoanFieldChange('original_principal', event.target.value)}
                  placeholder="0.00"
                />
              </label>
              <label>
                <span>Interest Rate %</span>
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  value={loanForm.interest_rate}
                  onChange={(event) => handleLoanFieldChange('interest_rate', event.target.value)}
                  placeholder="6.750"
                />
              </label>
              <label>
                <span>Term (years)</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={loanForm.loan_term_years}
                  onChange={(event) => handleLoanFieldChange('loan_term_years', event.target.value)}
                  placeholder="30"
                />
              </label>
              <label>
                <span>Monthly Payment (debt service)</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={loanForm.monthly_payment}
                  onChange={(event) => handleLoanFieldChange('monthly_payment', event.target.value)}
                  placeholder="Optional for cash flow"
                />
              </label>
              <label className="pd-loan-modal-field--full">
                <span>Notes</span>
                <textarea
                  rows="4"
                  value={loanForm.notes}
                  onChange={(event) => handleLoanFieldChange('notes', event.target.value)}
                  placeholder="Servicer notes, escrow notes, ARM details, balloon terms, etc."
                />
              </label>
            </div>

            <div className="pd-loan-modal-actions">
              {property.loan_id && (
                <button
                  type="button"
                  className="pd-loan-modal-btn pd-loan-modal-btn--danger"
                  onClick={handleDeleteLoan}
                  disabled={savingLoan || deletingLoan}
                >
                  {deletingLoan ? 'Deleting...' : 'Delete Loan'}
                </button>
              )}
              <button
                type="button"
                className="pd-loan-modal-btn pd-loan-modal-btn--secondary"
                onClick={() => {
                  setLoanForm(buildLoanForm(property));
                  setShowLoanModal(false);
                }}
                disabled={savingLoan || deletingLoan}
              >
                Cancel
              </button>
              <button
                type="button"
                className="pd-loan-modal-btn"
                onClick={handleSaveLoan}
                disabled={savingLoan || deletingLoan}
              >
                {savingLoan ? 'Saving...' : 'Save Loan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRentcastApiKeyModal && (
        <div className="pd-api-key-modal-backdrop" onClick={handleCloseRentcastApiKeyModal}>
          <div className="pd-api-key-modal" onClick={(event) => event.stopPropagation()}>
            <div className="pd-api-key-modal-header">
              <div>
                <div className="pd-api-key-modal-eyebrow">RentCast Access</div>
                <h3>Manage personal API key</h3>
              </div>
              <button type="button" className="pd-api-key-modal-close" onClick={handleCloseRentcastApiKeyModal}>
                <i className="pi pi-times" />
              </button>
            </div>

            <div className="pd-api-key-modal-copy">
              Your key is encrypted on the server and only used for this account. Automatic refresh is checked daily and calls RentCast only when your saved data is older than the setting below. You can press Run now anytime.
            </div>

            <div className="pd-api-key-summary">
              <div className={`pd-api-key-summary-line${hasRentcastApiKey ? ' pd-api-key-summary-line--saved' : ''}`}>
                {loadingRentcastApiKeyStatus
                  ? 'Loading saved key status...'
                  : hasRentcastApiKey
                    ? `Personal key saved${rentcastApiKeyUpdatedLabel ? ` · updated ${rentcastApiKeyUpdatedLabel}` : ''}`
                    : 'No personal key saved'}
              </div>
              <div className="pd-api-key-summary-line">
                {loadingRentcastApiKeyStatus ? 'Loading recent usage...' : rentcastRecentUsageSummary}
              </div>
            </div>

            {!loadingRentcastApiKeyStatus && hasRentcastApiKey && (
              <div className="pd-api-key-controls">
                <label className={`pd-api-key-toggle${showRentcastApiKeyEditor ? ' pd-api-key-toggle--disabled' : ''}`}>
                  <div className="pd-api-key-toggle__copy">
                    <strong>Automatic refresh</strong>
                    <small>{rentcastAutoRefreshEnabled ? 'Checked daily · refreshes only when due' : 'Off'}</small>
                  </div>
                  <span className="pd-api-key-toggle__switch">
                    <input
                      type="checkbox"
                      checked={rentcastAutoRefreshEnabled}
                      onChange={(event) => handleToggleRentcastAutoRefresh(event.target.checked)}
                      disabled={loadingRentcastApiKeyStatus || savingRentcastApiKey || deletingRentcastApiKey || showRentcastApiKeyEditor}
                    />
                    <span className="pd-api-key-toggle__slider" />
                  </span>
                </label>

                <div className="pd-api-key-inline-actions">
                  <button
                    type="button"
                    className="pd-api-key-inline-btn"
                    onClick={handleEditRentcastApiKey}
                    disabled={loadingRentcastApiKeyStatus || savingRentcastApiKey || deletingRentcastApiKey}
                  >
                    <i className="pi pi-pencil" />
                    Edit key
                  </button>
                  <button
                    type="button"
                    className="pd-api-key-inline-btn pd-api-key-inline-btn--danger"
                    onClick={handleDeleteRentcastApiKey}
                    disabled={loadingRentcastApiKeyStatus || savingRentcastApiKey || deletingRentcastApiKey}
                  >
                    Remove key
                  </button>
                </div>
              </div>
            )}

            {!loadingRentcastApiKeyStatus && (showRentcastApiKeyEditor || !hasRentcastApiKey) && (
              <div className="pd-api-key-editor">
                <label className="pd-api-key-field">
                  <span>{hasRentcastApiKey ? 'Replace API key (optional)' : 'Add API key'}</span>
                  <input
                    type="password"
                    value={rentcastApiKeyInput}
                    onChange={(event) => setRentcastApiKeyInput(event.target.value)}
                    placeholder={hasRentcastApiKey ? 'Enter a new RentCast API key' : 'Paste your RentCast API key'}
                    autoComplete="new-password"
                    disabled={loadingRentcastApiKeyStatus || savingRentcastApiKey || deletingRentcastApiKey}
                  />
                </label>

                <div className="pd-api-key-settings-grid">
                  <label className="pd-api-key-field">
                    <span>Only refresh if data is older than (days)</span>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      step="1"
                      value={rentcastRefreshFrequencyDays}
                      onChange={(event) => setRentcastRefreshFrequencyDays(event.target.value)}
                      disabled={loadingRentcastApiKeyStatus || savingRentcastApiKey || deletingRentcastApiKey}
                    />
                  </label>
                </div>

                <div className="pd-api-key-modal-actions">
                  <button
                    type="button"
                    className="pd-api-key-modal-btn pd-api-key-modal-btn--secondary"
                    onClick={handleCancelRentcastApiKeyEditor}
                    disabled={savingRentcastApiKey || deletingRentcastApiKey}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="pd-api-key-modal-btn"
                    onClick={handleSaveRentcastApiKey}
                    disabled={loadingRentcastApiKeyStatus || savingRentcastApiKey || deletingRentcastApiKey}
                  >
                    {savingRentcastApiKey ? 'Saving...' : hasRentcastApiKey ? 'Save Changes' : 'Save API Key'}
                  </button>
                </div>
              </div>
            )}

            <div className="pd-api-key-estimate-row">
              <div className="pd-api-key-estimate-note">
                <span className="pd-api-key-estimate-note__value">
                  {loadingRentcastApiKeyStatus ? 'Loading estimate...' : rentcastUsageSummary}
                </span>
              </div>
              {hasRentcastApiKey && (
                <button
                  type="button"
                  className="pd-api-key-run-btn"
                  onClick={handleRunRentcastNow}
                  disabled={loadingRentcastApiKeyStatus || savingRentcastApiKey || deletingRentcastApiKey || runningRentcastRefresh || showRentcastApiKeyEditor}
                >
                  <i className={`pi ${runningRentcastRefresh ? 'pi-spin pi-spinner' : 'pi-play'}`} />
                  {runningRentcastRefresh ? 'Running...' : 'Run now'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="pd-grid">

        {/* ── Rent Comparison ── */}
        <section className="pd-card pd-card--wide">
          <h2 className="pd-card-title">Rent Analysis</h2>
          <div className="pd-rent-toolbar">
            <span className={`pd-rent-source-pill ${contractRentAmount != null ? 'pd-rent-source-pill--contract' : ''}`}>
              {contractRentAmount != null ? 'Using contract rent' : 'Using latest ledger month'}
            </span>
            <div className="pd-rent-toolbar-actions">
              <button
                type="button"
                className="pd-rent-action-btn pd-rent-action-btn--icon"
                onClick={handleOpenRentcastApiKeyModal}
              >
                <i className="pi pi-pencil" /> API Key
              </button>
              <button
                type="button"
                className="pd-rent-action-btn"
                onClick={() => setShowContractRentEditor((current) => !current)}
              >
                {contractRentAmount != null ? 'Edit contract rent' : 'Set contract rent'}
              </button>
              {contractRentAmount != null && (
                <button
                  type="button"
                  className="pd-rent-action-btn pd-rent-action-btn--secondary"
                  onClick={handleClearContractRent}
                  disabled={savingContractRent}
                >
                  Use ledger rent
                </button>
              )}
            </div>
          </div>

          {showContractRentEditor && (
            <div className="pd-rent-editor">
              <div className="pd-rent-editor-copy">
                <div className="pd-rent-editor-title">Current contract rent</div>
                <div className="pd-rent-editor-sub">
                  {latestLedgerRentMonthLabel && latestLedgerRentAmount != null
                    ? `Latest ledger rent total is ${fmt(latestLedgerRentAmount)}/mo for ${latestLedgerRentMonthLabel}.`
                    : 'No rent ledger month has been recorded yet.'}
                </div>
              </div>
              <div className="pd-rent-editor-controls">
                <label className="pd-rent-input-wrap">
                  <span>$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={contractRentInput}
                    onChange={(event) => setContractRentInput(event.target.value)}
                    placeholder={latestLedgerRentAmount != null ? String(latestLedgerRentAmount) : '0'}
                  />
                </label>
                <button
                  type="button"
                  className="pd-rent-save-btn"
                  onClick={handleSaveContractRent}
                  disabled={savingContractRent}
                >
                  {savingContractRent ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  className="pd-rent-cancel-btn"
                  onClick={() => {
                    setContractRentInput(property?.contract_rent != null ? String(property.contract_rent) : '');
                    setShowContractRentEditor(false);
                  }}
                  disabled={savingContractRent}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className={`pd-rent-comparison ${rentVariance != null ? 'pd-rent-comparison--with-variance' : ''}`}>
            <div className="pd-rent-side">
              <div className="pd-rent-big-label">Your Rent</div>
              <div className="pd-rent-big-val green">{displayedRentAmount != null ? fmt(displayedRentAmount) : '—'}<span className="pd-rent-unit">/mo</span></div>
              <div className="pd-rent-big-sub">
                {contractRentAmount != null
                  ? `current contract rent${contractRentUpdatedLabel ? ` · updated ${contractRentUpdatedLabel}` : ''}`
                  : latestLedgerRentMonthLabel
                    ? `latest ledger rent total · ${latestLedgerRentMonthLabel}`
                    : 'No rent line item yet'}
              </div>
              {contractRentAmount != null && latestLedgerRentAmount != null && latestLedgerRentMonthLabel && (
                <div className="pd-rent-big-sub pd-rent-big-sub--secondary">
                  Latest ledger total: {fmt(latestLedgerRentAmount)}/mo · {latestLedgerRentMonthLabel}
                </div>
              )}
            </div>
            <div className="pd-rent-divider" />
            <div className="pd-rent-side">
              <div className="pd-rent-big-label">Market Rent</div>
              <div className="pd-rent-big-val">{marketRent ? fmt(marketRent) : '—'}{marketRent && <span className="pd-rent-unit">/mo</span>}</div>
              <div className="pd-rent-big-sub pd-rent-market-meta">
                <span>{marketRentUpdatedLabel ? `Updated ${marketRentUpdatedLabel}` : 'Not yet fetched'}</span>
                {marketRentRangeLabel && <span className="pd-rent-range-pill">Range {marketRentRangeLabel}</span>}
              </div>
              <div className="pd-rent-source-line">
                Data pulled from <a href={buildRentCastUrl(property)} target="_blank" rel="noreferrer">RentCast.io</a>
              </div>
            </div>
            {rentVariance != null && (
              <div className="pd-rent-side pd-rent-side--verdict">
                <div className={`pd-rent-verdict ${rentVariance >= 0 ? 'pd-rent-verdict--above' : 'pd-rent-verdict--below'}`}>
                  {rentVariance >= 0 ? '↑ Above Market' : '↓ Below Market'}
                </div>
                <div className={`pd-rent-variance ${rentVariance >= 0 ? 'green' : 'red'}`}>
                  {rentVariance >= 0 ? '+' : ''}{fmt(rentVariance)}<span className="pd-rent-unit">/mo</span>
                </div>
                <div className="pd-rent-big-sub">{rentPct?.toFixed(1)}% of market rate</div>
              </div>
            )}
          </div>

          {/* Stats row */}
          {/* {marketRent && (
            <div className="pd-dep-stats" style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #F3F4F6' }}>
              {marketRent && <div className="pd-stat"><span className="pd-stat-label">Annual Market Rent</span><span className="pd-stat-value">{fmt(marketRent * 12)}</span></div>}
              {grossYield && <div className="pd-stat"><span className="pd-stat-label">Gross Yield</span><span className="pd-stat-value">{grossYield.toFixed(2)}%</span></div>}
              {displayedRentAmount != null && marketRent && (
                <div className="pd-stat">
                  <span className="pd-stat-label">{contractRentAmount != null ? 'Annualized Contract Rent' : 'Annualized Ledger Rent'}</span>
                  <span className="pd-stat-value green">{fmt(displayedRentAmount * 12)}</span>
                </div>
              )}
            </div>
          )} */}

          {/* Bar comparison */}
          {/* {marketRent && displayedRentAmount != null && (() => {
            const max = Math.max(displayedRentAmount, marketRent) * 1.15;
            return (
              <div className="pd-rent-bars">
                <div className="pd-rent-bar-row">
                  <span className="pd-rent-bar-label">{contractRentAmount != null ? 'Contract' : 'Ledger'}</span>
                  <div className="pd-rent-bar-track"><div className="pd-rent-bar pd-rent-bar--actual" style={{ width: `${(displayedRentAmount / max) * 100}%` }} /></div>
                  <span className="pd-rent-bar-val green">{fmt(displayedRentAmount)}</span>
                </div>
                <div className="pd-rent-bar-row">
                  <span className="pd-rent-bar-label">Market</span>
                  <div className="pd-rent-bar-track"><div className="pd-rent-bar pd-rent-bar--market" style={{ width: `${(marketRent / max) * 100}%` }} /></div>
                  <span className="pd-rent-bar-val">{fmt(marketRent)}</span>
                </div>
              </div>
            );
          })()} */}
        </section>

        {/* ── Valuation Trend ── */}
        {/* {valuations.length > 0 && (
          <section className="pd-card pd-card--wide">
            <h2 className="pd-card-title">Home Value & Market Rent Trends <span className="pd-card-subtitle">Rentcast history</span></h2>
            <div className="pd-chart-wrap">
              <Line
                data={{
                  labels: valuations.map(v => new Date(v.fetched_at).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })),
                  datasets: [
                    { label: 'Estimated Value', data: valuations.map(v => v.estimated_value), borderColor: '#3B82F6', backgroundColor: 'rgba(59,130,246,0.08)', fill: true, tension: 0.3, yAxisID: 'value' },
                    { label: 'Market Rent', data: valuations.map(v => v.estimated_rent), borderColor: '#16A34A', backgroundColor: 'rgba(22,163,74,0.08)', fill: true, tension: 0.3, yAxisID: 'rent' },
                  ],
                }}
                options={{
                  responsive: true, maintainAspectRatio: false,
                  interaction: { mode: 'index', intersect: false },
                  plugins: { legend: { position: 'bottom' } },
                  scales: {
                    value: { type: 'linear', position: 'left', ticks: { callback: v => `$${(v / 1000).toFixed(0)}k` } },
                    rent: { type: 'linear', position: 'right', grid: { drawOnChartArea: false }, ticks: { callback: v => `$${v.toLocaleString()}` } },
                  },
                }}
              />
            </div>
            <p className="pd-disclaimer">* Updated automatically each month via Rentcast AVM.</p>
          </section>
        )} */}
        
         {/* ── Income Expenses Actuals Chart ── */}
        <section className="pd-card pd-card--wide pd-income-expense-card">
          <div className="pd-chart-card-head">
            <div>
              <h2 className="pd-card-title pd-card-title--chart">Income vs Operating Expenses</h2>
              <p className="pd-card-subcopy pd-card-subcopy--chart">
                Expected income, actual ledger income, and operating expenses by month. Owner distributions are excluded from operating expenses.
              </p>
            </div>
            <label className="pd-chart-filter">
              <span>Range</span>
              <select value={incomeExpenseRange} onChange={(event) => setIncomeExpenseRange(event.target.value)}>
                {INCOME_EXPENSE_RANGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>
          {incomeExpenseMonths.length > 0 ? (
            <>
              <div className="pd-chart-wrap pd-chart-wrap--income-expense">
                <Bar data={incomeExpenseChart} options={incomeExpenseOpts} />
              </div>
              <div className="pd-income-expense-foot">
                <span>{incomeExpenseWindowLabel ? `Showing ${incomeExpenseWindowLabel}.` : 'Showing selected calendar months.'}</span>
                <span className="pd-income-expense-foot-note">Expected income is based on {expectedIncomeSource || 'contract rent'}.</span>
              </div>

            </>
          ) : (
            <p className="pd-empty">No income or operating expense history yet.</p>
          )}
        </section>



        {/* ── Fees ── */}
        {/* <section className="pd-card pd-card--wide pd-fee-audit-card">
          <div className="pd-chart-card-head">
            <div>
              <h2 className="pd-card-title pd-card-title--chart">Property Management Fees by Month</h2>
              <p className="pd-card-subcopy pd-card-subcopy--chart">
                Compares the expected 7% management fee against actual management company fees and highlights possible duplicate or unusual charges.
              </p>
            </div>
            <label className="pd-chart-filter">
              <span>Range</span>
              <select value={pmFeeAuditRange} onChange={(event) => setPmFeeAuditRange(event.target.value)}>
                {PM_FEE_AUDIT_RANGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>
          {pmFeeAuditRows.length > 0 ? (
            <>
              <div className="pd-fee-audit-table-wrap">
                <table className="pd-table pd-fee-audit-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Expected Mgmt Fee 7%</th>
                      <th>Actual Mgmt Fee</th>
                      <th>Eviction Fees</th>
                      <th>Renewal Fees</th>
                      <th>Advertising Fees</th>
                      <th>Other Fees</th>
                      <th>Total PM Fees</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pmFeeAuditRows.map((row) => (
                      <tr key={row.month} className={`pd-fee-audit-row pd-fee-audit-row--${row.status.tone}`}>
                        <td>{row.monthLabel}</td>
                        <td className="pd-fee-audit-num">{fmt(row.expectedManagementFee)}</td>
                        <td className="pd-fee-audit-num">{fmt(row.actualManagementFee)}</td>
                        <td className="pd-fee-audit-num">{fmt(row.evictionFees)}</td>
                        <td className="pd-fee-audit-num">{fmt(row.renewalFees)}</td>
                        <td className="pd-fee-audit-num">{fmt(row.advertisingFees)}</td>
                        <td className="pd-fee-audit-num">{fmt(row.otherFees)}</td>
                        <td className="pd-fee-audit-num pd-fee-audit-total">{fmt(row.totalPmFees)}</td>
                        <td>
                          <span className={`pd-fee-audit-status pd-fee-audit-status--${row.status.tone}`}>
                            {row.status.label}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="pd-income-expense-foot">
                <span>{pmFeeAuditWindowLabel ? `Showing ${pmFeeAuditWindowLabel}.` : 'Showing selected calendar months.'}</span>
                <span className="pd-income-expense-foot-note">Expected fee assumes monthly rent income × 7.00%.</span>
              </div>
            </>
          ) : (
            <p className="pd-empty">No management-company fee activity found for the selected period.</p>
          )}
        </section> */}

        {/* ── Statement Review ── */}
        <section className="pd-card pd-card--wide">
          <StatementReviewPanel propertyId={id} embedded={true} externalToast={toast} />
        </section>

        {/* ── Depreciation ── */}
        <section className="pd-card pd-card--wide">
          <h2 className="pd-card-title">Depreciation Schedule</h2>
          <div className="pd-dep-stats pd-dep-schedule-stats">
            <div className="pd-stat"><span className="pd-stat-label">Useful Life</span><span className="pd-stat-value">{dep.usefulLife} years</span></div>
            <div className="pd-stat"><span className="pd-stat-label">Depreciable Base</span><span className="pd-stat-value">{fmt(dep.depreciableBase)}</span></div>
            <div className="pd-stat"><span className="pd-stat-label">Annual Deduction</span><span className="pd-stat-value">{fmt(dep.annualDeduction)}</span></div>
            <div className="pd-stat"><span className="pd-stat-label">Accumulated</span><span className="pd-stat-value">{fmt(dep.accumulatedDepreciation)}</span></div>
            <div className="pd-stat"><span className="pd-stat-label">Years Remaining</span><span className="pd-stat-value">{dep.yearsRemaining.toFixed(1)}</span></div>
          </div>
          {(() => {
            const depData = depreciationChartData(dep, property.purchase_date);
            const currentYear = new Date().getFullYear();
            const currentIdx = depData.labels.indexOf(currentYear);
            const currentVal = currentIdx >= 0 ? depData.datasets[0].data[currentIdx] : null;
            return (
              <>
                <div className="pd-chart-wrap">
                  <Line data={depData} options={{
                    ...chartOpts,
                    plugins: { ...chartOpts.plugins, tooltip: { callbacks: {
                      title: (items) => `Year ${items[0].label}`,
                      label: (item) => ` $${Number(item.raw).toLocaleString()}${String(item.label) === String(currentYear) ? ' ← you are here' : ''}`,
                    }}},
                    scales: {
                      x: { grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { maxTicksLimit: 10, color: (ctx) => String(ctx.tick.label) === String(currentYear) ? '#7C3AED' : '#9CA3AF', font: (ctx) => ({ weight: String(ctx.tick.label) === String(currentYear) ? '700' : '400', size: 11 }) } },
                      y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { callback: v => `$${(v / 1000).toFixed(0)}k`, color: '#9CA3AF', font: { size: 11 } } },
                    },
                  }} />
                </div>
                {currentVal != null && (
                  <div className="pd-dep-current-callout">
                    <span className="pd-dep-dot" />
                    <span><strong>{currentYear}</strong> — ${Number(currentVal).toLocaleString(undefined, { maximumFractionDigits: 0 })} accumulated ({((currentVal / dep.depreciableBase) * 100).toFixed(1)}% of depreciable base)</span>
                  </div>
                )}
              </>
            );
          })()}
          <p className="pd-disclaimer">* Estimate only. Based on IRS straight-line depreciation with 80% depreciable basis. Consult a tax professional.</p>
        </section>



        {/* ── Loan Amortization ── */}
        {amort && (
          <section className="pd-card pd-card--wide">
            <h2 className="pd-card-title">Loan Amortization</h2>
            <div className="pd-dep-stats pd-amort-stats">
              <div className="pd-stat"><span className="pd-stat-label">Lender</span><span className="pd-stat-value">{property.lender_name || '—'}</span></div>
              <div className="pd-stat"><span className="pd-stat-label">Original Loan</span><span className="pd-stat-value">{fmt(property.original_principal)}</span></div>
              <div className="pd-stat"><span className="pd-stat-label">Rate</span><span className="pd-stat-value">{property.interest_rate}%</span></div>
              <div className="pd-stat"><span className="pd-stat-label">Monthly P&I</span><span className="pd-stat-value">{fmt(amort.payment)}</span></div>
              {amort.hasPaymentOverride && (
                <div className="pd-stat"><span className="pd-stat-label">Debt Service</span><span className="pd-stat-value">{fmt(amort.debtServicePayment)}</span></div>
              )}
              <div className="pd-stat"><span className="pd-stat-label">Principal Paid</span><span className="pd-stat-value green">{fmt(amort.principalPaid)}</span></div>
              <div className="pd-stat"><span className="pd-stat-label">Interest Paid</span><span className="pd-stat-value red">{fmt(amort.interestPaid)}</span></div>
              <div className="pd-stat"><span className="pd-stat-label">Remaining Balance</span><span className="pd-stat-value">{fmt(amort.remainingBalance)}</span></div>
            </div>
            <div className="pd-chart-wrap">
              <Chart type="bar" data={amort.chartData} options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => ` ${ctx.dataset.label}: ${fmt(ctx.raw)}`,
                      title: (items) => {
                        const label = items[0].label;
                        if (label === 'Start') return 'Loan Start';
                        const yr = Number.parseInt(label, 10);
                        const currentYear = new Date().getFullYear();
                        const payoffYear = amort.payoffDate.getFullYear();
                        if (yr === currentYear) return `${label} · Today`;
                        if (yr === payoffYear) return `${label} · Paid Off`;
                        return label;
                      },
                    },
                  },
                },
                scales: {
                  x: {
                    stacked: true,
                    grid: { display: false },
                    border: { display: false },
                    ticks: {
                      color: '#64748B',
                      font: { size: 11, weight: '600' },
                      maxRotation: 0,
                      autoSkip: true,
                      maxTicksLimit: 10,
                      callback: function tickLabel(value) {
                        const label = this.getLabelForValue(value);
                        return label === 'Start' ? String(amort.startYear) : label;
                      },
                    },
                  },
                  payments: {
                    axis: 'y',
                    stacked: true,
                    beginAtZero: true,
                    position: 'right',
                    border: { display: false },
                    grid: { drawOnChartArea: false },
                    ticks: { color: '#64748B', font: { size: 11 }, callback: (v) => `$${Number(v).toLocaleString()}` },
                  },
                  balance: {
                    axis: 'y',
                    beginAtZero: true,
                    position: 'left',
                    border: { display: false },
                    grid: { color: 'rgba(148,163,184,0.14)' },
                    ticks: { color: '#64748B', font: { size: 11 }, callback: (v) => `$${Number(v).toLocaleString()}` },
                  },
                },
              }} />
            </div>
            <div className="pd-amort-legend">
              <span className="pd-amort-legend-item pd-amort-legend-item--balance"><span />Remaining balance</span>
              <span className="pd-amort-legend-item pd-amort-legend-item--principal"><span />Annual principal</span>
              <span className="pd-amort-legend-item pd-amort-legend-item--interest"><span />Annual interest</span>
              <span className="pd-amort-legend-item pd-amort-legend-item--today"><span />Today ({new Date().getFullYear()}) · {fmt(amort.remainingBalance)} remaining</span>
              <span className="pd-amort-legend-item pd-amort-legend-item--payoff"><span />Paid off {amort.payoffDate.getFullYear()} · {amort.monthsRemaining} mo left</span>
            </div>
            {amort.hasPaymentOverride && (
              <p className="pd-disclaimer">* Amortization uses calculated principal and interest from the loan terms. Saved monthly payment is still used as debt service for cash-flow metrics.</p>
            )}
          </section>
        )}
{/*
        <section className="pd-card">
          <h2 className="pd-card-title">Expense Trend by Category</h2>
          <p className="pd-card-subcopy">Stacked monthly operating expenses for the latest ledger months.</p>
          {expenseTrendDatasets.length > 0 ? (
            <>
              <div className="pd-chart-wrap pd-chart-wrap--sm">
                <Bar data={expenseTrendChart} options={expenseTrendOpts} />
              </div>
              <div className="pd-expense-trend-summary">
                {rankedExpenseCategories.slice(0, 4).map(([category, amount], index) => (
                  <div key={category} className="pd-expense-chip">
                    <span
                      className="pd-expense-chip-swatch"
                      style={{ backgroundColor: EXPENSE_TREND_COLORS[index % EXPENSE_TREND_COLORS.length] }}
                    />
                    <span className="pd-expense-chip-label">{formatCategoryLabel(category)}</span>
                    <strong>{fmt(amount)}</strong>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="pd-empty">No operating expense history yet.</p>
          )}
        </section>

        <section className="pd-card">
          <h2 className="pd-card-title">Rent vs Market vs Contract</h2>
          <p className="pd-card-subcopy">Compare what was collected in the latest ledger month against contract and market rent.</p>
          {rentBenchmarkItems.length > 0 ? (
            <>
              <div className="pd-chart-wrap pd-chart-wrap--sm">
                <Bar data={rentBenchmarkChart} options={rentBenchmarkOpts} />
              </div>
              <div className="pd-rent-benchmark-grid">
                {rentBenchmarkItems.map((item) => (
                  <div key={item.key} className="pd-rent-benchmark-item">
                    <span className="pd-rent-benchmark-label">{item.label}</span>
                    <strong style={{ color: RENT_BENCHMARK_COLORS[item.key] }}>{fmt(item.value)}</strong>
                    <span className="pd-rent-benchmark-sub">{item.sublabel}</span>
                  </div>
                ))}
              </div>
              <div className="pd-rent-benchmark-notes">
                {collectedVsContractDelta != null && (
                  <div className={`pd-rent-benchmark-note ${collectedVsContractDelta >= 0 ? 'pd-rent-benchmark-note--good' : 'pd-rent-benchmark-note--alert'}`}>
                    Collected vs contract: {collectedVsContractDelta >= 0 ? '+' : ''}{fmt(collectedVsContractDelta)}/mo
                  </div>
                )}
                {contractVsMarketDelta != null && (
                  <div className={`pd-rent-benchmark-note ${contractVsMarketDelta >= 0 ? 'pd-rent-benchmark-note--good' : 'pd-rent-benchmark-note--alert'}`}>
                    Contract vs market: {contractVsMarketDelta >= 0 ? '+' : ''}{fmt(contractVsMarketDelta)}/mo
                  </div>
                )}
                {contractRentAmount == null && (
                  <div className="pd-rent-benchmark-note">
                    Set contract rent above to compare collected rent against the active lease.
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="pd-empty">No rent benchmarks are available yet.</p>
          )}
        </section> */}

        {/* ── Recent Expenses ── */}
        {/* <section className="pd-card">
          <h2 className="pd-card-title">Recent Expenses</h2>
          {expenses.length === 0 ? <p className="pd-empty">No expenses recorded yet.</p> : (
            <table className="pd-table">
              <thead><tr><th>Date</th><th>Category</th><th>Vendor</th><th>Amount</th></tr></thead>
              <tbody>{expenses.slice(0, 10).map(e => (
                <tr key={e.id}>
                  <td>{e.expense_date?.slice(0, 10)}</td>
                  <td><span className="pd-tag">{e.category}</span></td>
                  <td>{e.vendor || '—'}</td>
                  <td className="red">{fmt(e.amount)}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </section> */}

        {/* ── Income History ── */}
        {/* <section className="pd-card">
          <h2 className="pd-card-title">Income History</h2>
          {income.length === 0 ? <p className="pd-empty">No income recorded yet.</p> : (
            <table className="pd-table">
              <thead><tr><th>Month</th><th>Category</th><th>Amount</th></tr></thead>
              <tbody>{income.slice(0, 10).map(r => (
                <tr key={r.id}>
                  <td>{r.period_month?.slice(0, 7)}</td>
                  <td><span className="pd-tag">{r.category}</span></td>
                  <td className="green">{fmt(r.amount)}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </section> */}

      </div>
    </div>
  );
};

export default PropertyDetail;
